<!-- eslint-disable vue/multi-word-component-names -- component name is capitalised, and unlikely that html will introduce a similar tag -->
<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="rcp-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="right-container">
      <div class="top-section">
        <div class="filter-desc">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
          :buttonIsDisabled="mainFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterReceiptByDate"
            >
              <div>
                <DateTimePicker
                  placeholder="From"
                  :isRequired="filterReceiptByDate"
                  :showTime="false"
                  :isDisabled="!filterReceiptByDate"
                  :value.sync="receiptListFromDate"
                  :errorMessage.sync="fromDateErrMsg"
                />
                <DateTimePicker
                  placeholder="To"
                  :isRequired="filterReceiptByDate"
                  :showTime="false"
                  :isDisabled="!filterReceiptByDate"
                  :value.sync="receiptListToDate"
                  :errorMessage.sync="toDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterReceiptBySearch"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterReceiptBySearch"
                  :isRequired="filterReceiptBySearch"
                  :selectedObject.sync="selectedSearchBy"
                  :errorMessage.sync="searchByErr"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterReceiptBySearch"
                  :isRequired="filterReceiptBySearch"
                  :selectedObject.sync="selectedSearchCriteria"
                  :errorMessage.sync="searchCriteriaErr"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchValue"
                  :isDisabled="!filterReceiptBySearch"
                  :isRequired="filterReceiptBySearch"
                  :errorMessage.sync="searchValueErr"
                />
              </div>
            </BoxContainer>
            <button @click="refreshClicked" :disabled="!filterReceiptByDate && !filterReceiptBySearch">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          title="Receipt"
          :tableData="receiptListing"
          :selectedRow.sync="selectedReceiptListing"
          :loading="receiptTableBoxLoading"
          :dataType="receiptListingDataType"
          style="height: 100%"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
      :canPrint="false"
      @print="popupPrintClicked"
    >
      <div class="receipt-entries">
        <!-- Summary of Events -->
        <BoxContainer
          v-if="action != 'N'"
          title="Summary of Events"
          :isCollapsible="true"
          :isCollapsed="summaryIsCollapsed"
        >
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in showSummaryItems" :key="i">
              <Tooltip class="milestone-tooltip" :minWidth="210" :tooltipText="m['Label'].join(', ')">
                <div></div>
              </Tooltip>
              <div
                :id="m['Title']"
                v-if="m['Title'] != 'Print Count' && m['Title'] != 'Reinstate'"
                class="summary-item"
              >
              </div>
              <div
                v-if="
                  m['Title'] == 'Reinstate' &&
                  receiptDetails['LatestAudit'] == 'R' &&
                  receiptDetails['Indicator'] != 'C'
                "
                class="summary-item"
                :style="{ backgroundColor: m['BgColor'] }"
              >
                Reinstated
              </div>
              <div v-if="m['Title'] == 'Print Count'" class="summary-item">
                {{ receiptDetails['Print Count'] }}
                <img :src="m['Src']" height="20px" />
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(bg, val, i) in newLegend" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: `${bg}` }"></div>
              {{ val }}
            </div>
          </div>
        </BoxContainer>

        <!-- Receipt Header -->
        <BoxContainer title="Receipt Header" :isCollapsible="true" style="padding-bottom: 5px">
          <div class="receipt-header">
            <div class="header-containers" style="width: 50%">
              <Dropdown
                ref="receipttype"
                placeholder="Receipt Type"
                :items="receiptTypeList"
                itemKey="Description"
                :selectedObject.sync="selectedReceiptType"
                :errorMessage.sync="receiptTypeErr"
                :isRequired="true"
                :isDisabled="(viewMode && action != 'N') || selectedReceiptType != null"
                @selected="receiptTypeSelected"
                style="width: 75%"
                validator="text,50"
              />
              <InputQuery
                ref="custno"
                placeholder="Customer No."
                :items="customerList"
                itemKey="Code - Customer"
                :selectedObject.sync="selectedCustomer"
                :errorMessage.sync="customerErr"
                :isRequired="true"
                :isDisabled="viewMode || offsetDetails.length > 0 || disableHeader"
                validator="text,105"
              />
              <InputQuery
                ref="paytype"
                placeholder="Payment Type"
                :items="paymentTypeList"
                itemKey="Payment Type - Description"
                :selectedObject.sync="selectedPaymentType"
                :errorMessage.sync="paymentTypeErr"
                :isRequired="true"
                :isDisabled="viewMode || disableHeader"
                @selected="paymentTypeSelected"
                validator="text,15"
              />
              <BoxContainer title="Cheque Info">
                <Input
                  ref="chequeno"
                  placeholder="Cheque No."
                  :inputValue.sync="chequeNo"
                  :errorMessage.sync="chequeNoErr"
                  :isRequired="showChequeInfo"
                  :isDisabled="viewMode || !showChequeInfo || disableHeader"
                  validator="text,25,,spacedash"
                />
                <DateTimePicker
                  ref="chequedate"
                  placeholder="Cheque Date"
                  :value.sync="chequeDate"
                  :isRequired="showChequeInfo"
                  :showTime="false"
                  :errorMessage.sync="chequeDateErr"
                  :maxDate="new Date('2079-06-06')"
                  :isDisabled="viewMode || !showChequeInfo || disableHeader"
                />
                <Input
                  ref="chequebank"
                  placeholder="Cheque Bank"
                  :inputValue.sync="chequeBank"
                  :errorMessage.sync="chequeBankErr"
                  :isDisabled="viewMode || !showChequeInfo || disableHeader"
                  validator="text,10"
                />
              </BoxContainer>
            </div>
            <div class="header-containers">
              <div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 5px">
                <RadioButtonSet
                  ref="txntype"
                  title="Transaction Type"
                  :buttons="txnType"
                  labelKey="label"
                  :selected.sync="selectedTxnType"
                  :errorMessage.sync="txnTypeErr"
                  :isHorizontal="true"
                  :isRequired="true"
                  :isDisabled="viewMode || disableHeader"
                  style="margin-right: 5px"
                />
                <InputQuery
                  ref="bank"
                  placeholder="Bank In Bank"
                  :showDesc="false"
                  :items="bankList"
                  itemKey="BankCode - BankName"
                  :selectedObject.sync="selectedBank"
                  :errorMessage.sync="bankErr"
                  :isRequired="true"
                  :isDisabled="viewMode || disableHeader"
                  @selected="bankSelected"
                  style="width: 50%; margin-left: 5px"
                  validator="text,215"
                />
              </div>
              <div class="foreign-currency-main" style="margin: 5px 0px">
                <div class="foreign-currency-controls">
                  <div class="foreign-currency-row">
                    <div class="foreign-currency-column"></div>
                    <div class="foreign-currency-column" style="text-align: center"></div>
                    <div class="foreign-currency-column" style="text-align: center">MYR Equivalent</div>
                  </div>
                  <BoxContainer background="rgba(225, 255, 255)">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <InputQuery
                            ref="currency"
                            placeholder="Currency"
                            :showDesc="false"
                            :items="currencyList"
                            itemKey="Currency Code"
                            :selectedObject.sync="selectedCurrency"
                            :errorMessage.sync="currencyErr"
                            :isRequired="true"
                            :isDisabled="viewMode || offsetDetails.length > 0 || disableHeader"
                            @selected="currencySelected"
                            validator="text,3"
                          />
                          <Input
                            ref="factor"
                            placeholder="Exch. Factor"
                            :inputValue.sync="exchFactor"
                            :errorMessage.sync="exchFactorErr"
                            :isDisabled="true"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="payamt"
                            placeholder="Payment Amt."
                            :inputValue.sync="paymentFAmt"
                            :errorMessage.sync="paymentFAmtErr"
                            :isRequired="true"
                            @focusChanged="headerForeignCurrLossFocus($event, 'paymentFAmt', paymentFAmt)"
                            :isDisabled="viewMode || disableHeader"
                            validator="positiveDecimal,14,4"
                            style="padding-left: 5px"
                          />
                          <Input
                            ref="rate"
                            placeholder="Exch. Rate"
                            :inputValue.sync="exchRate"
                            :errorMessage.sync="exchRateErr"
                            :isRequired="true"
                            @focusChanged="headerForeignCurrLossFocus($event, 'exchRate', exchRate)"
                            :isDisabled="viewMode || disableExchRate || disableHeader"
                            validator="positiveDecimal,14,4"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="myramt"
                            placeholder="Local Amt."
                            :inputValue.sync="paymentAmt"
                            :errorMessage.sync="paymentAmtErr"
                            :isRequired="true"
                            validator="positiveDecimal,14,4"
                            @focusChanged="headerForeignCurrLossFocus($event, 'paymentAmt', paymentAmt)"
                            :isDisabled="viewMode || disableHeader"
                            style="padding-left: 5px"
                          />
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer background="rgba(225, 255, 255)">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row"></div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="bankchrg"
                            placeholder="F. Bank Charges"
                            :inputValue.sync="bankFCharge"
                            :errorMessage.sync="bankFChargeErr"
                            @focusChanged="headerForeignCurrLossFocus($event, 'fBankChrg', bankFCharge)"
                            :isDisabled="viewMode || disableFBankChrg || disableHeader"
                            validator="positiveDecimal,14,4"
                            style="padding: 0px 5px"
                          />
                          <Input
                            ref="rate"
                            placeholder="Exch. Rate"
                            :inputValue.sync="exchRate"
                            :errorMessage.sync="exchRateErr"
                            :isRequired="true"
                            :isDisabled="true"
                            validator="positiveDecimal,14,4"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="myramt"
                            placeholder="Local Bank Charges"
                            :inputValue.sync="bankCharge"
                            :errorMessage.sync="bankChargeErr"
                            @focusChanged="headerForeignCurrLossFocus($event, 'bankChrg', bankCharge)"
                            :isDisabled="viewMode || disableHeader"
                            validator="positiveDecimal,14,4"
                            style="padding-left: 5px"
                          />
                          <div
                            @click="autoCalculateBankChrg(paymentAmt)"
                            :style="{ 'margin-bottom': '5px', cursor: viewMode ? 'default' : 'pointer' }"
                          >
                            <img
                              src="https://dsmlib.logistics.systems/Icons/icons8-calculator-90.png"
                              alt="auto calculate bank charge"
                              width="25px"
                              height="25px"
                              title="Auto Calculate Bank Charge"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer background="rgba(225, 255, 255)" v-if="showTax && GSTEffective">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <InputQuery
                            ref="taxcode"
                            placeholder="Tax Code"
                            :items="taxCodeList"
                            itemKey="Tax Code - Description"
                            :selectedObject.sync="selectedTaxCode"
                            @selected="taxCodeSelected"
                            :isDisabled="viewMode || disableHeader"
                            validator="text,215"
                          />
                          <Input
                            ref="taxrate"
                            placeholder="Tax Rate"
                            :inputValue.sync="taxRate"
                            :errorMessage.sync="taxRateErr"
                            :isDisabled="viewMode || disableHeader"
                            validator="positiveInteger,4"
                            @focusChanged="headerForeignCurrLossFocus($event, 'taxRate', taxRate)"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="ftaxamt"
                            placeholder="F. Tax Amt."
                            :inputValue.sync="fTaxAmt"
                            :errorMessage.sync="fTaxAmtErr"
                            :isDisabled="true"
                            validator="decimal,14,4"
                            style="padding: 0px 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="taxamt"
                            placeholder="Tax Amt"
                            :inputValue.sync="taxAmt"
                            :errorMessage.sync="taxAmtErr"
                            :isDisabled="true"
                            validator="decimal,14,4"
                            style="padding-left: 5px"
                          />
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                </div>
                <div class="foreign-currency-background">
                  <div class="background-left"></div>
                  <div class="background-middle"></div>
                  <div class="background-right"></div>
                </div>
              </div>
            </div>
            <div class="header-containers" style="width: 50%">
              <Input
                ref="receiptno"
                placeholder="Receipt No"
                :inputValue.sync="receiptNo"
                :errorMessage.sync="receiptNoErr"
                :isRequired="true"
                :isDisabled="action != 'N' || disableReceiptNo || disableHeader"
                validator="text,25"
              />
              <DateTimePicker
                ref="receiptdate"
                placeholder="Receipt Date"
                :value.sync="receiptDate"
                :errorMessage.sync="receiptDateErr"
                :showTime="false"
                :isRequired="true"
                :isDisabled="viewMode || disableHeader"
                :maxDate="new Date('2079-06-06')"
              />
              <RemarksBox
                title="Remarks"
                :text.sync="remarks"
                :isDisabled="viewMode || disableHeader"
                :maxLength="200"
                style="min-height: 65%; max-height: 70%"
              />
            </div>
          </div>
        </BoxContainer>

        <div style="height: 100%; margin-top: 10px">
          <Tab
            :preventTabSwitch="true"
            :tabs="tabsItems"
            :tabIndex.sync="currentTabIndex"
            @tabClicked="tabClicked"
            style="height: 100%"
          >
            <div slot="Details" class="tab-slot">
              <div class="tab-details-styling" :style="{ width: showAllDocs || showOffsetInput ? '50%' : '100%' }">
                <div class="tab-buttons">
                  <button v-if="!viewMode || action != ''" @click="openOffsetDocsSelection" :disabled="showAllDocs"
                    >Add Offset Documents</button
                  >
                  <!-- <button v-if="!viewMode || action != ''" @click="openOffsetInput" :disabled="showOffsetInput">Add Offset</button> -->
                  <button v-if="!viewMode || action != ''" @click="deleteOffset">Delete Offset</button>
                </div>
                <TableBox
                  title="Offset Details"
                  v-if="showOffsetDetailsList"
                  :tableData="offsetDetails"
                  :selectedRow.sync="selectedOffsetDetails"
                  :loading="offsetDetailsLoading"
                  :additionalButtons="tableButtons"
                  @buttonClicked="offsetDetailsTableButtonClicked"
                  :maxSelection="1"
                  :hideColumns="['Receipt No', 'Outstanding', 'F.Outstanding', 'SeqNo', 'Action']"
                  :dataType="offsetDetailsTableDataType"
                  style="height: calc(100% - 40px)"
                />
              </div>

              <div v-if="showAllDocs" class="side-entry-open" :style="{ width: showAllDocs ? '50%' : '0%' }">
                <div>
                  <div style="position: sticky; z-index: 40; top: 0; background-color: white; padding-bottom: 15px">
                    <div class="title-button">
                      <b>Select Offset Documents</b>
                      <div style="display: flex" class="buttons-arr">
                        <button @click="selectDeselectOffsetDocsClicked">{{ selectDeselectOffsetDocs }} All</button>
                        <button @click="addMultiOffsetDocs">Add Documents</button>
                        <button @click="cancelSelectMultiDocs">Cancel</button>
                      </div>
                    </div>
                    <div style="display: flex; height: auto; justify-content: right; margin-right: 10px">
                      <b>Total Selected Amount </b>
                      <div style="margin-left: 5px">
                        <div class="total-block"
                          >{{ selectedCurrency['Currency Code'] }} {{ totalSelectedOffsetFAmt }}</div
                        >
                        <div class="total-block" style="background-color: #467cdc; color: white; font-weight: bold"
                          >MYR {{ totalSelectedOffsetAmt }}</div
                        >
                      </div>
                    </div>
                  </div>

                  <div style="overflow: scroll; margin-top: 10px">
                    <TableBox
                      title="Offset Documents"
                      :tableData="filterOffsetDocsListing"
                      :selectedRow.sync="selectedOffsetDocs"
                      :loading="offsetDocsListLoading"
                      :maxSelection="-1"
                      :hideColumns="[]"
                      :additionalButtons="[{ text: 'Edit Offset', index: 0, width: 100 }]"
                      @buttonClicked="editOffsetAmtBtnClicked"
                      style="height: calc(100% - 40px)"
                      :dataType="offsetDocsListingDataType"
                    />
                  </div>
                </div>
              </div>

              <div v-if="showOffsetInput" class="side-entry-open" :style="{ width: showOffsetInput ? '50%' : '0%' }">
                <div class="title-button">
                  <b>Edit Offset</b>
                  <div class="buttons-arr">
                    <button @click="editOffsetClicked">Edit</button>
                    <button @click="cancelOffsetClicked">Cancel</button>
                  </div>
                </div>
                <div style="margin-right: 10px">
                  <div style="display: flex; margin-bottom: 10px">
                    <div style="width: 100%; margin-right: 10px; padding-top: 10px">
                      <InputQuery
                        ref="docno"
                        placeholder="Doc No."
                        :items="offsetDocsListing"
                        itemKey="Offset Doc"
                        :selectedObject.sync="selectedDoc"
                        :errorMessage.sync="docNoErr"
                        :showDesc="false"
                        @selected="docSelected"
                        style="align-self: center"
                        :isDisabled="true"
                        validator="text,25"
                      />
                      <Input placeholder="Doc Rate" :inputValue.sync="docExchRate" :isDisabled="true" />
                    </div>
                    <RemarksBox title="Remarks" :text.sync="offsetRemarks" :isDisabled="viewMode" :maxLength="200" />
                  </div>

                  <div class="foreign-currency-main">
                    <div class="foreign-currency-controls">
                      <div class="foreign-currency-row">
                        <div class="foreign-currency-column"></div>
                        <div class="foreign-currency-column" style="text-align: center"></div>
                        <div class="foreign-currency-column" style="text-align: center">MYR Equivalent</div>
                      </div>
                      <BoxContainer background="rgba(225, 255, 255)">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <InputQuery
                                ref="offsetcurr"
                                placeholder="Currency"
                                :items="currencyList"
                                itemKey="Currency Code"
                                :selectedObject.sync="selectedCurrency"
                                :isRequired="true"
                                :isDisabled="true"
                                validator="text,3"

                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="docfos"
                                :placeholder="`F. Outstanding`"
                                :inputValue.sync="docFOutstanding"
                                :isDisabled="true"
                                validator="decimal,14,4"
                                style="padding-left: 5px"
                              />
                              <Input
                                ref="docrate"
                                placeholder="Exch. Rate"
                                :inputValue.sync="exchRate"
                                :isDisabled="true"
                                validator="decimal,14,4"
                                style="padding-right: 5px"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="docos"
                                :placeholder="`Outstanding`"
                                :inputValue.sync="docOutstanding"
                                :isDisabled="true"
                                style="padding-left: 5px"
                                validator="decimal,14,4"
                              />
                            </div>
                          </div>
                        </div>
                      </BoxContainer>
                      <BoxContainer background="rgba(225, 255, 255)">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <InputQuery
                                ref="offsetcurr"
                                placeholder="Currency"
                                :items="currencyList"
                                itemKey="Currency Code"
                                :selectedObject.sync="selectedCurrency"
                                :isRequired="true"
                                :isDisabled="true"
                                validator="text,3"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="offsetfamt"
                                placeholder="Offset F. Amt."
                                :inputValue.sync="offsetFAmt"
                                :errorMessage.sync="offsetFAmtErr"
                                :isDisabled="viewMode"
                                validator="decimal,14,4"
                                style="padding-left: 5px"
                                @focusChanged="offsetFocusChanged($event, 'famt', offsetFAmt)"
                              />
                              <Input
                                ref="offsetrate"
                                placeholder="Exch. Rate"
                                :inputValue.sync="exchRate"
                                :isDisabled="true"
                                validator="decimal,14,4"
                                style="padding-right: 5px"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="offsetamt"
                                placeholder="Offset Amt."
                                :inputValue.sync="offsetAmt"
                                :errorMessage.sync="offsetAmtErr"
                                :isDisabled="viewMode"
                                style="padding-left: 5px"
                                validator="decimal,14,4"
                                @focusChanged="offsetFocusChanged($event, 'amt', offsetAmt)"
                              />
                            </div>
                          </div>
                        </div>
                      </BoxContainer>
                    </div>
                    <div class="foreign-currency-background">
                      <div class="background-left"></div>
                      <div class="background-middle"></div>
                      <div class="background-right"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div slot="Attachments" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <div style="display: flex; justify-content: end; margin-top: 5px">
                  <button v-if="!viewMode" @click="openAttachmentPopup('new')" style="margin-right: 10px"
                    >Add Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="openAttachmentPopup('edit')"
                    style="margin-right: 10px"
                    >Edit Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="deleteAttachmentClicked"
                    style="margin-right: 10px"
                    >Del Attachment</button
                  >
                </div>
                <TableBox
                  v-if="showAttTable"
                  title="Attachment"
                  :tableData="attachmentsList"
                  :selectedRow.sync="selectedAtt"
                  :additionalButtons="[{ text: 'View', index: 0, width: 60 }]"
                  @buttonClicked="attAdditionalBtnsClicked"
                  :hideColumns="[
                    'Action',
                    'Doc Image',
                    'Sequence No',
                    'Document GUID',
                    'ID',
                    'Doc Ref No',
                    'Job No',
                    'Doc Code',
                    'Doc Type',
                    'Doc Rec Date',
                    'Doc Rec Time',
                    '_attachmentAction',
                  ]"
                  :dataType="{ 'Doc Date': 'date' }"
                />
              </div>
            </div>
            <div slot="Events" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <BoxContainer title="GL Entry">
                  <TableBox
                    :tableData="glEntryList"
                    title="GL Entry"
                    :maxSelection="0"
                    style="height: auto"
                    :dataType="glEntryTableDataType"
                  />
                  <div style="display: flex; justify-content: space-around; margin-top: 10px">
                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL DEBIT</div>
                      <div class="total-block" style="margin-left: 5px">{{ totalDebit }}</div>
                    </div>

                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL CREDIT</div>
                      <div class="total-block" style="margin-left: 5px">{{ totalCredit }}</div>
                    </div>
                  </div>
                </BoxContainer>
              </div>
            </div>
          </Tab>
        </div>
      </div>

      <BoxContainer>
        <div style="display: flex; flex-direction: row; justify-content: flex-end">
          <div style="display: flex; margin-right: 20px">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">Total Offset</div>
            <div style="display: flex">
              <div class="total-block" style="font-size: 1.1em"
                >{{ selectedCurrency ? selectedCurrency['Currency Code'] : 'MYR' }} {{ totalFOffset }}</div
              >
              <div
                class="total-block"
                style="background-color: #467cdc; color: white; font-size: 1.1em; font-weight: bold"
                >MYR {{ totalOffset }}</div
              >
            </div>
          </div>
          <div style="display: flex">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">Receipt Balance</div>
            <div style="display: flex">
              <div class="total-block" style="font-size: 1.1em"
                >{{ selectedCurrency ? selectedCurrency['Currency Code'] : 'MYR' }} {{ receiptFBalance }}</div
              >
              <div
                class="total-block"
                style="background-color: #467cdc; color: white; font-size: 1.1em; font-weight: bold"
                >MYR {{ receiptBalance }}</div
              >
            </div>
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>

    <PopupV2
      :show="showDeleteReceipt"
      title="Delete Receipt"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div
        >Please state reason to delete
        {{ selectedReceiptListing.length > 0 ? selectedReceiptListing[0]['Receipt No'] : '' }}?</div
      >
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deleteReceiptRemarks"
        :errorMessage.sync="deleteReceiptRemarksErr"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="attachmentsPopupBtnsClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            @fileChanged="handleFileUpload"
            :errorMessage.sync="attachmentsRequiredErrorMessage"
          />
          <div v-if="attachmentsAction == 'E'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment({
                  'File Name': selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '',
                  'MIME Type': null,
                  'File Data': selectedAtt.length > 0 ? selectedAtt[0]['Doc Image'] : '',
                })
              "
            >
              {{ selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '' }}
            </span>
          </div>
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>
    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedAtt[0]['File Type'].replaceAll('.', '').toUpperCase() }} files cannot be previewed. <br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    // So that summary UI reinstate part does not render if no reinstate
    showSummaryItems: function (){
      const copyOfSummaryItems = [...this.summaryItems];
      if (this.receiptDetails['LatestAudit'] && this.receiptDetails['LatestAudit'].trim() == 'R') {

        const reinstateItem = {
          Title: 'Reinstate',
          Label: [
            this.receiptDetails['LastAuditUser'],
            new Date(this.receiptDetails['AuditDate']).formatString('yyyy-MM-dd HH:mm'),
          ],
          Indicator: [],
          BgColor: '#79d279',
        };

        copyOfSummaryItems.splice(2, 0, reinstateItem);

      }
      const reconcillation = copyOfSummaryItems.find((item) => item['Title'] == 'recon');

      if(this.receiptDetails['recon'] === 'S'){
        reconcillation['Label'] = [this.receiptDetails['statement'], this.receiptDetails['statementDate']];
      }
      else{
        reconcillation['Label'] = ['Reconcilled', 'Unreconcilled'];
      }

      return copyOfSummaryItems;
    },
    // Enable cheque info fields when payment type is Cheque (CH)
    showChequeInfo: function () {
      return this.selectedPaymentType && this.selectedPaymentType['Payment Type'] == 'CH';
    },

    // Only Advance payment will show tax fields
    showTax: function () {
      return this.selectedTxnType && this.selectedTxnType['keyValue'] == 'A';
    },

    tableButtons: function () {
      return this.action == '' ? [] : [{ text: 'Edit', index: 0, width: 80 }];
    },

    selectDeselectOffsetDocs: function () {
      return this.filterOffsetDocsListing.length > 0 && this.selectedOffsetDocs.length == this.filterOffsetDocsListing.length ?
          'Deselect'
        : 'Select';
    },

    // User not allowed to change exchange rate when already add offset
    disableExchRate: function () {
      return (
        (this.selectedCurrency && this.selectedCurrency['Currency Code'] == 'MYR') || this.offsetDetails.length > 0
      );
    },

    // Enable foreign bank charge if bank is in foreign currency
    disableFBankChrg: function () {
      return this.selectedBank && this.selectedBank['BankCurrency'] == 'MYR';
    },

    // Calculate total Foreign offset based on offset details list
    totalFOffset: function () {
      if (this.offsetDetails.length > 0) {
        const total = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['F.Offset Amount']), 0);
        return isNaN(total) ? '0.00' : this.$currencyFormat(total);
      }
      return '0.00';
    },

    // Calculate total offset (MYR) based on offset details list
    totalOffset: function () {
      if (this.offsetDetails.length > 0) {
        const total = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['Offset Amount']), 0);
        return isNaN(total) ? '0.00' : this.$currencyFormat(total);
      }
      return '0.00';
    },

    // Calculate total debit in GL Entry
    totalDebit: function () {
      if (this.glEntryList.length > 0) {
        const debits = this.glEntryList.filter((entry) => entry['Debit Ac'] != '0');
        const total = debits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },

    // Calculate total credit in GL Entry
    totalCredit: function () {
      if (this.glEntryList.length > 0) {
        const credits = this.glEntryList.filter((entry) => entry['Credit Ac'] != '0');
        const total = credits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },

    // Documents that are already added into the receipt offset details list should not appear in the offset docs listing
    filterOffsetDocsListing: function () {
      if (this.offsetDetails.length == 0) return this.offsetDocsListing;
      const offsetDocNo = this.offsetDetails.map((d) => d['Doc No']);
      return this.offsetDocsListing.filter((doc) => !offsetDocNo.includes(doc['Offset Doc']));
    },

    // To calculate the total foreign offset amount for the selected offset documents (before adding into the offset details list)
    totalSelectedOffsetFAmt: function () {
      if (this.selectedOffsetDocs.length == 0) return '0.00';
      const allAmount = this.selectedOffsetDocs.map((doc) => doc['F.Offset Amount'].toString().replaceAll(',', ''));
      const total = allAmount.reduce((acc, amt) => acc + Number(amt), 0);
      return isNaN(total) ? '0.00' : this.$currencyFormat(total);
    },

    // To calculate the total offset amount for the selected offset documents (before adding into the offset details list)
    totalSelectedOffsetAmt: function () {
      if (this.selectedOffsetDocs.length == 0) return '0.00';
      const allAmount = this.selectedOffsetDocs.map((doc) => doc['Offset Amount'].toString().replaceAll(',', ''));
      const total = allAmount.reduce((acc, amt) => acc + Number(amt), 0);
      return isNaN(total) ? '0.00' : this.$currencyFormat(total);
    },

    // Hide tax fields if not applicable based on SST date and GST date
    GSTEffective: function () {
      return (
        this.companyGSTEnabled == '1' &&
        new Date(this.companyGSTEffectiveDate) <= new Date(this.receiptDate) &&
        new Date(this.companySSTEffectiveDate) > new Date(this.receiptDate)
      );
    },
  },
  data: function () {
    return {
      menu: [
        {
          title: 'New Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'mnuNew',
        },
        {
          title: 'Edit Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'mnEdit',
        },
        {
          title: 'Delete Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'mnuDelete',
        },
        { title: 'View Receipt', icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png', enabled: false },
        {
          title: 'Authorize Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnAuthorized',
          visible: true,
        },
        {
          title: 'Unauthorize Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnUnAuthorized',
          visible: false,
        },
        {
          title: 'Reinstate Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-loop-50.png',
          enabled: false,
          key: 'mnuReinStateReceipt',
        },
        {
          title: 'Print Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
          key: 'mnuReceipt',
        },
      ],
      viewMode: false,
      action: '',
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Details', disabled: false },
        { title: 'Attachments', disabled: false },
        { title: 'Events', disabled: false },
      ],

      filterText: '',
      mainFilterIsOpen: false,
      mainFilterIsDisabled: false,
      filterReceiptByDate: true,
      receiptListFromDate: new Date(),
      fromDateErrMsg: '',
      receiptListToDate: new Date(),
      toDateErrMsg: '',

      filterReceiptBySearch: false,
      searchFilterItems: [
        { 'Search By': 'Receipt #' },
        { 'Search By': 'Customer #' },
        { 'Search By': 'Customer Name' },
        { 'Search By': 'Salesman Name' },
        { 'Search By': 'Bank' },
        { 'Search By': 'Cheque #' },
        { 'Search By': 'Cheque Bank' },
      ],
      selectedSearchBy: null,
      searchByErr: '',
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedSearchCriteria: null,
      searchCriteriaErr: '',
      searchValue: '',
      searchValueErr: '',
      lastRefreshed: '',

      receiptListing: [],
      selectedReceiptListing: [],
      receiptTableBoxLoading: false,
      receiptListingDataType: {
        Total: 'currency',
        Balance: 'currency',
      },

      showPopup: false,
      popupTitle: '',
      createdBy: '',
      updatedBy: '',
      requiredError: [],
      successMessages: [],
      errorMessages: [],

      //#region Summary of Events
      summaryIsCollapsed: false,
      summaryItems: [
        {
          Title: 'Indicator',
          Label: ['New', 'Printed', 'Cancelled'],
          Indicator: [
            { code: 'N', name: 'New Receipt', bg: '#79d279' },
            { code: 'P', name: 'Printed', bg: '#79d279' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
          ],
        },
        {
          Title: 'Authorized',
          Label: ['Authorized', 'Unauthorize'],
          Indicator: [
            { code: 'A', name: 'Authorized', bg: '#79d279' },
            { code: 'U', name: 'Unauthorize', bg: '#ffff99' },
          ],
        },
        {
          Title: 'recon',
          Label: ['Reconcilled', 'Unreconcilled'],
          Indicator: [
            { code: 'S', name: 'Reconcilled', bg: '#79d279' },
            { code: '', name: 'Unreconcilled', bg: '#ffff99'}
          ],
        },
        {
          Title: 'Print Count',
          Src: 'https://dsmlib.logistics.systems/Icons/Print.png',
          Label: ['Print Count'],
          Indicator: [{ code: '0', name: 'Print Count' }],
        }
      ],
      newLegend: {
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Completed: '#79d279',
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Pending: '#ffff99',
      },
      //#endregion

      //#region Receipt Details
      receiptDetails: [],
      allowEditRt: true,

      // Lists
      receiptTypeList: [],
      customerList: [],
      paymentTypeList: [],
      bankList: [],
      txnType: [
        { label: 'Account Payment', keyValue: 'N' },
        { label: 'Advance Payment', keyValue: 'A' },
      ],
      currencyList: [],
      taxCodeList: [],

      // Receipt Header Data
      disableHeader: false,
      receiptNo: '',
      receiptNoErr: '',
      receiptDate: new Date(),
      receiptDateErr: '',
      remarks: '',
      disableReceiptNo: true,
      selectedReceiptType: null,
      receiptTypeErr: '',
      selectedCustomer: null,
      customerErr: '',
      selectedPaymentType: null,
      paymentTypeErr: '',

      selectedTxnType: null,
      txnTypeErr: '',
      selectedBank: null,
      bankErr: '',
      defaultChqBank: '',
      defaultCashBank: '',

      receiptFBalance: '0.00',
      receiptBalance: '0.00',

      // Cheque Info
      chequeNo: '',
      chequeNoErr: '',
      chequeDate: null,
      chequeDateErr: '',
      chequeBank: '',
      chequeBankErr: '',

      // Foreign Currency Section Data
      selectedCurrency: null,
      currencyErr: '',
      oldRate: '',
      exchRate: '',
      exchRateErr: '',
      exchFactor: '',
      exchFactorErr: '',
      oldFAmt: '0.00',
      paymentFAmt: '0.00',
      paymentFAmtErr: '', // foreign amt
      oldAmt: '0.00',
      paymentAmt: '0.00',
      paymentAmtErr: '', // local amt
      bankFCharge: '0.00',
      bankFChargeErr: '', // foreign bank charge
      bankCharge: '0.00',
      bankChargeErr: '', // local bank charge
      selectedTaxCode: null,
      taxRate: '0',
      taxRateErr: '',
      fTaxAmt: '0.00',
      fTaxAmtErr: '',
      taxAmt: '0.00',
      taxAmtErr: '',

      // Offset Details Tab
      saveOffsetAction: '',
      showAllDocs: false,
      showOffsetInput: false,
      offsetDetails: [],
      offsetDetailsTableDataType: {
        'Offset Amount': 'currency',
        'F.Offset Amount': 'currency',
        'Date Updated': 'date',
        'Doc Rate': 'currency',
      },
      showOffsetDetailsList: true,
      selectedOffsetDetails: [],
      deletedOffsetDetails: [],
      offsetDetailsLoading: false,

      offsetDocsListing: [],
      selectedOffsetDocs: [],
      offsetDocsListLoading: false,
      offsetDocsListingDataType: {
        'Doc Date': 'date',
        'Offset Amount': 'currency',
        'F.Offset Amount': 'currency',
        Outstanding: 'currency',
        'F.Outstanding': 'currency',
        'Doc Rate': 'currency',
        'Original Amt': 'currency',
        'F.Original Amt': 'currency',
      },
      editFromOffsetListing: false,

      selectedDoc: null,
      docNoErr: '',
      docExchRate: '',
      offsetRemarks: '',
      docFOutstanding: '0.0000',
      docOutstanding: '0.0000',
      offsetFAmt: '0.0000',
      offsetFAmtErr: '',
      offsetAmt: '0.0000',
      offsetAmtErr: '',

      attachmentsList: [],
      deletedAttList: [],
      showAttTable: true,
      selectedAtt: [],
      showAttachmentsPopup: false,
      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],
      documents: {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      oldDoc: '',
      attachmentsAction: '', // 'N', 'E', 'V'
      attachmentsRequiredErrorMessage: '',
      showAttachmentProjector: false,
      isProjectable: false,
      attachmentProjectorFileName: '',
      attachmentProjectorData: '',
      attachmentProjectorType: '',

      glEntryList: [],
      glEntryTableDataType: {
        'Ref No': 'string',
        'Debit No': 'string',
        'Credit No': 'string',
        Amount: 'currency',
        Vehicle: 'string',
        'Cost Code': 'string',
        Remarks: 'string',
      },

      showDeleteReceipt: false,
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      deleteReceiptRemarks: '',
      deleteReceiptRemarksErr: '',

      companyGSTEnabled: '0',
      companySSTEffectiveDate: '', // Company.co_gsteffectivedate
      companyGSTEffectiveDate: '', // Company.co_gst2effectivedate
      customerFieldValue: "",
      currencyFieldValue: ""
    };

  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearAllData();
      this.clearAllMessages();
      this.mainFilterIsOpen = false;
      this.currentTabIndex = 0;

      switch (e['title']) {
        case 'New Receipt':
          this.action = 'N';
          this.showPopup = true;
          this.saveOffsetAction = 'N';
          if (this.receiptTypeList.length == 1) {
            this.selectedReceiptType = this.receiptTypeList[0];
            this.viewMode = false;
          } else {
            this.viewMode = true;
          }
          this.receiptNo = this.disableReceiptNo ? '**********' : '';
          this.receiptDate = new Date();
          this.selectedTxnType = this.txnType.find((t) => t['keyValue'] == 'N');
          this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == 'MYR');
          this.currencySelected(this.selectedCurrency);
          this.popupTitle = e['title'];
          if (!this.selectedReceiptType) {
            this.$nextTick(() => {
              this.$refs['receipttype'].setFocus();
            });
          }
          break;

        case 'Edit Receipt': {
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'Edit Receipt', 'Please select a receipt to edit');
            return;
          }

          if (this.selectedReceiptListing[0].Indicator == 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Already Cancelled',
              'You are not allowed to edit cancelled receipt!',
            );
            return;
          }
          const editable = await this.receiptEditable(this.selectedReceiptListing[0]['Receipt No']);
          if (!editable['editable'] && editable['header'].includes('SST')) {
            // Cannot edit receipt if already filled SST
            this.$showAlert('error', editable['header'], editable['msg']);
            return;
          } else if (!editable['editable']) {
            // Check if settingssystem allow edit receipt (except when SST filled)
            if (!this.allowEditRt) {
              this.$showAlert('error', editable['header'], editable['msg']);
              return;
            }
            this.disableHeader = true;
          }
          this.action = 'E';
          this.saveOffsetAction = 'E';
          this.viewMode = false;
          await this.getReceipt(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            this.loadSummaryOfEvents();
          });
          break;
        }

        case 'View Receipt':
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'View Receipt', 'Please select a receipt to view');
            return;
          }
          this.action = '';
          this.viewMode = true;
          await this.getReceipt(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            this.loadSummaryOfEvents();
          });
          break;

        case 'Delete Receipt': {
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'Delete Receipt', 'Please select a receipt to delete');
            return;
          }

          if (this.selectedReceiptListing[0].Indicator == 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Already Cancelled',
              'You are not allowed to delete cancelled receipt!',
            );
            return;
          }

          const editable = await this.receiptEditable(this.selectedReceiptListing[0]['Receipt No']);
          if (!editable['editable']) {
            this.$showAlert('error', editable['header'], editable['msg']);
            return;
          }

          this.action = 'D';
          this.deleteReceiptRemarks = '';
          this.deleteReceiptRemarksErr = '';
          this.showDeleteReceipt = true;
          break;
        }

        case 'Reinstate Receipt': {
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'Reinstate Receipt', 'Please select a receipt to reinstate');
            return;
          }

          if (this.selectedReceiptListing[0].Indicator != 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Not Cancelled',
              'You are only allowed to reinstate cancelled receipt!',
            );
            return;
          }

          const editable = await this.receiptEditable(this.selectedReceiptListing[0]['Receipt No']);
          if (!editable['editable']) {
            this.$showAlert('error', editable['header'], editable['msg']);
            return;
          }

          const confirm = await this.$showAlert(
            'warning',
            'Reinstate Receipt',
            `Are you sure you want to reinstate for this Receipt ${this.selectedReceiptListing[0]['Receipt No']}?`,
            [{ text: 'Yes', color: 'red', textColor: 'white' }, { text: 'No' }],
          );

          if (confirm == 'Yes') {
            this.$fullscreenLoading('Reinstating Receipt');
            const rt = this.selectedReceiptListing[0];
            const dataToPost = {
              action: '',
              receiptno: rt['Receipt No'],
              receiptdate: new Date(rt['Receipt Date']).formatString('yyyy-MM-dd'),
              billcu_no: rt['Customer No'],
              reinstate: '1',
            };
            const results = await this.$apiCallV2('/Receipt/SaveReceipt', dataToPost);
            this.$fullscreenLoading();

            if (results[0]['Results'] == 0) {
              this.$showAlert('success', 'Reinstated!', `Receipt ${rt['Receipt No']} was successfully reinstated.`);
            } else {
              this.$showAlert('error', 'Reinstate Unsuccessful', `${results[0]['ErrMsg']}`);
            }
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Authorize Receipt': {
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'Authorize Receipt', 'Please select a receipt to authorize!');
            return;
          } else if (this.selectedReceiptListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Already Cancelled',
              'You are not allowed to authorize cancelled receipt!',
            );
            return;
          }

          const confirmAuth = await this.$showAlert(
            'warning',
            'Authorize Receipt',
            `Are you sure you want to authorize for this Receipt ${this.selectedReceiptListing[0]['Receipt No']}?`,
            [
              { text: 'Yes', color: 'green', textColor: 'white' },
              { text: 'No', color: 'red' },
            ],
          );
          if (confirmAuth == 'No') return;

          this.$fullscreenLoading('Authorizing Receipt');
          const dataToAuth = {
            action: 'A',
            docrefname: 'RECEIPT',
            docrefid: this.selectedReceiptListing[0]['Receipt No'],
            date: new Date(this.selectedReceiptListing[0]['Receipt Date']).formatString('yyyy-MM-dd'),
          };
          const authorizeResult = await this.$apiCallV2('/Receipt/Authorization', dataToAuth);
          this.$fullscreenLoading();

          if (authorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Authorized',
              `Receipt ${this.selectedReceiptListing[0]['Receipt No']} was successfully authorized!`,
            );
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          } else {
            this.$showAlert('error', 'Authorization Unsuccessful', `${authorizeResult[0]['ErrMsg']}`);
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Unauthorize Receipt': {
          if (this.selectedReceiptListing.length == 0) {
            this.$showAlert('error', 'Unauthorize Receipt', 'Please select a receipt to unauthorize!');
            return;
          } else if (this.selectedReceiptListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Already Cancelled',
              'You are not allowed to unauthorize cancelled receipt!',
            );
            return;
          }

          const confirmUnauth = await this.$showAlert(
            'warning',
            'Authorize Receipt',
            `Are you sure you want to unauthorize for this Receipt ${this.selectedReceiptListing[0]['Receipt No']}?`,
            [
              { text: 'Yes', color: 'green', textColor: 'white' },
              { text: 'No', color: 'red' },
            ],
          );
          if (confirmUnauth == 'No') return;

          this.$fullscreenLoading('Unauthorizing Receipt');
          const dataToUnauth = {
            action: 'U',
            docrefname: 'RECEIPT',
            docrefid: this.selectedReceiptListing[0]['Receipt No'],
            date: new Date(this.selectedReceiptListing[0]['Receipt Date']).formatString('yyyy-MM-dd'),
          };
          const unauthorizeResult = await this.$apiCallV2('/Receipt/Authorization', dataToUnauth);
          this.$fullscreenLoading();

          if (unauthorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Unauthorized',
              `Receipt ${this.selectedReceiptListing[0]['Receipt No']} was successfully unauthorized!`,
            );
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          } else {
            this.$showAlert('error', 'Unauthorization Unsuccessful', `${unauthorizeResult[0]['ErrMsg']}`);
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Print Receipt':
          if (this.selectedReceiptListing.length == 0) {
            this.openPrintReceipt();
          } else if (this.selectedReceiptListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Receipt Already Cancelled',
              'You are not allowed to print cancelled receipt',
            );
          } else {
            this.openPrintReceipt(this.selectedReceiptListing[0]['Receipt No']);
          }
          break;
      }
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showAllDocs && tabIndex != 0) {
        if (this.selectedOffsetDocs.length > 0) {
          const discard = await this.promptDiscardOffsetDoc();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showAllDocs = false;
          this.selectedOffsetDocs = [];
        });
      }

      if (this.showOffsetInput && tabIndex != 0) {
        const discard = await this.promptDiscardOffsetInput();
        if (discard === 'Cancel') {
          return;
        }
        this.$nextTick(() => {
          this.showOffsetInput = false;
          this.clearAllOffsetDetails();
        });
      }
      this.currentTabIndex = tabIndex;
    },

    receiptEditable: async function (receiptno) {
      this.$fullscreenLoading('Checking Receipt Editable');
      const results = (await this.$apiCallV2('/Receipt/ReceiptEditable', { rtno: receiptno }))?.['Receipt Editable'][
        'Editable Info'
      ];
      this.$fullscreenLoading();
      if (results['IsClose'] == '1') {
        let msg = '';
        if (this.allowEditRt == 'N') {
          msg = 'You are not allowed to edit or cancel this receipt!';
        } else {
          msg = 'You are not allowed to edit receipt header or cancel this receipt!';
        }
        return { editable: false, header: 'Month End Already Processed', msg: msg };
      }

      if (results['type'] == 'L') {
        return {
          editable: false,
          header: 'Receipt Already Post to GL',
          msg: 'You are not allowed to edit receipt header or cancel this receipt!',
        };
      }

      if (results['RtRecon'] == 'S') {
        return {
          editable: false,
          header: 'Receipt was Recon in Bank Statement',
          msg: 'You are not allowed to edit receipt or cancel this receipt!',
        };
      }

      if (results['AdjEntry'] == '2') {
        return {
          editable: false,
          header: 'This is an Opening Receipt',
          msg: 'You are not allowed to edit receipt or cancel this receipt!',
        };
      }

      if (results['GST'] == 'N') {
        return {
          editable: false,
          header: 'GST Already Paid',
          msg: 'You are not allowed to edit receipt header or cancel this receipt!',
        };
      }

      if (results['SST'] == '1') {
        return {
          editable: false,
          header: 'SST Already Paid',
          msg: 'You are not allowed to edit receipt or cancel this receipt!',
        };
      }

      return { editable: true, header: '', msg: '' };
    },

    promptDiscardOffsetDoc: async function () {
      return await this.$showAlert(
        'warning',
        'Selected Offset Documents not added yet',
        'Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    promptDiscardOffsetInput: async function () {
      return await this.$showAlert(
        'warning',
        'Edited Offset Amount not saved yet',
        'Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    refreshClicked: async function () {
      let receiptListingErr = false;
      if (this.filterReceiptByDate) {
        if (!this.receiptListFromDate) {
          this.fromDateErrMsg = 'Enter from date!';
          this.receiptListingErr = true;
        }

        if (!this.receiptListToDate) {
          this.toDateErrMsg = 'Enter to date!';
          receiptListingErr = true;
        }
      }

      if (this.filterReceiptBySearch) {
        if (!this.selectedSearchBy) {
          this.searchByErr = 'Select search by!';
          receiptListingErr = true;
        }

        if (!this.selectedSearchCriteria) {
          this.searchCriteriaErr = 'Select search criteria!';
          receiptListingErr = true;
        }

        if (!this.searchValue.trim()) {
          this.searchValueErr = 'Enter search value!';
          receiptListingErr = true;
        }
      }
      if (receiptListingErr) return;

      this.mainFilterIsOpen = false;
      const dataToPost = {
        startdate:
          this.filterReceiptByDate && this.receiptListFromDate ?
            this.receiptListFromDate.formatString('yyyy-MM-dd')
          : '',
        enddate:
          this.filterReceiptByDate && this.receiptListToDate ? this.receiptListToDate.formatString('yyyy-MM-dd') : '',
        searchfield: this.filterReceiptBySearch ? this.selectedSearchBy['Search By'] : '',
        searchcriteria: this.filterReceiptBySearch ? this.selectedSearchCriteria['name'] : '',
        searchvalue: this.filterReceiptBySearch ? this.searchValue : '',
        rttype: '0',
      };

      const filterItems = [];
      if (this.filterReceiptByDate) {
        filterItems.push(
          `date from ${this.receiptListFromDate.formatString('yyyy-MM-dd')} to ${this.receiptListToDate.formatString('yyyy-MM-dd')}`,
        );
      }
      if (this.filterReceiptBySearch) {
        filterItems.push(
          `${this.selectedSearchBy['Search By']} ${this.selectedSearchCriteria['name']} ${this.searchValue}`,
        );
      }
      this.filterText = `Filter by ${filterItems.join(', ')}`;

      this.$fullscreenLoading('Getting Receipt Listing');
      this.receiptTableBoxLoading = true;
      this.receiptListing = [];
      this.selectedReceiptListing = [];
      this.receiptListing = await this.$apiCallV2('/Receipt/GetReceiptListing', dataToPost);
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
      this.receiptTableBoxLoading = false;
      this.$fullscreenLoading();
    },

    checkInputFields: function () {
      let error = false;

      if (!this.selectedReceiptType) {
        this.receiptTypeErr = 'Receipt Type is required!';
        this.requiredError.push({ key: 'receipttype', value: this.receiptTypeErr });
        error = true;
      }

      if (!this.selectedCustomer) {
        this.customerErr = 'Customer is required!';
        this.requiredError.push({ key: 'custno', value: this.customerErr });
        error = true;
      }

      if (!this.selectedPaymentType) {
        this.paymentTypeErr = 'Payment Type is required!';
        this.requiredError.push({ key: 'paytype', value: this.paymentTypeErr });
        error = true;
      }

      if (this.showChequeInfo) {
        if (!this.chequeNo.trim()) {
          this.chequeNoErr = 'Cheque No is required!';
          this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
          error = true;
        }

        if (!this.chequeNo.trim().length > 25) {
          this.chequeNoErr = 'Cheque No cannot exceed 25 characters!';
          this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
          error = true;
        }

        if (!this.chequeDate) {
          this.chequeDateErr = 'Cheque Date is required!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          error = true;
        } else if (this.chequeDate && this.chequeDate.setHours(0, 0, 0, 0) > new Date('2079-06-06')) {
          this.chequeDateErr = 'Cheque Date Invalid!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          error = true;
        }

        if (!this.chequeBank.trim().length > 200) {
          this.chequeBankErr = 'Cheque Bank cannot exceed 200 characters!';
          this.requiredError.push({ key: 'chequebank', value: this.chequeBankErr });
          error = true;
        }
      }

      if (!this.selectedTxnType) {
        this.txnTypeErr = 'Transaction Type is required!';
        this.requiredError.push({ key: 'txntype', value: this.txnTypeErr });
        error = true;
      }

      if (!this.selectedBank) {
        this.bankErr = 'Bank is required!';
        this.requiredError.push({ key: 'bank', value: this.bankErr });
        error = true;
      }

      if (!this.selectedCurrency) {
        this.currencyErr = 'Currency is required!';
        this.requiredError.push({ key: 'currency', value: this.currencyErr });
        error = true;
      }

      if (!this.paymentFAmt.trim()) {
        this.paymentFAmtErr = 'Payment Amount is required!';
        this.requiredError.push({ key: 'payamt', value: this.paymentFAmtErr });
        error = true;
      } else {
        const split = this.paymentFAmt.split('.');
        if (split[0].length > 14 || split[1].length > 4) {
          this.paymentFAmtErr = 'Payment Amount cannot exceed 14 digits and 4 decimal places!';
          this.requiredError.push({ key: 'payamt', value: this.paymentFAmtErr });
          error = true;
        }
      }

      if (!this.exchRate.trim()) {
        this.exchRateErr = 'Exchange Rate is required!';
        this.requiredError.push({ key: 'rate', value: this.exchRateErr });
        error = true;
      }

      if (!this.paymentAmt.trim()) {
        this.paymentAmtErr = 'Payment Amount is required!';
        this.requiredError.push({ key: 'myramt', value: this.paymentAmtErr });
        error = true;
      } else {
        const split = this.paymentAmt.split('.');
        if (split[0].length > 14 || split[1].length > 4) {
          this.paymentAmtErr = 'Payment Amount cannot exceed 14 digits and 4 decimal places!';
          this.requiredError.push({ key: 'myramt', value: this.paymentAmtErr });
          error = true;
        }
      }

      if (!this.receiptDate) {
        this.receiptDateErr = 'Receipt Date is required!';
        this.requiredError.push({ key: 'receiptdate', value: this.receiptDateErr });
        error = true;
      } else if (this.receiptDate && new Date(this.receiptDate).setHours(0, 0, 0, 0) > new Date('2079-06-06')) {
        this.receiptDateErr = 'Receipt Date Invalid!';
        this.requiredError.push({ key: 'receiptdate', value: this.receiptDateErr });
        error = true;
      }

      return error;
    },

    saveClicked: async function () {
      //#region Validations
      // Check required fields
      this.errorMessages = [];
      this.requiredError = [];
      this.clearAllMessages();

      if (this.showOffsetInput) {
        const discard = await this.promptDiscardOffsetInput();
        if (discard === 'Cancel') return;

        this.$nextTick(() => {
          this.showOffsetInput = false;
          this.clearAllOffsetDetails();
        });
      }

      if (this.showAllDocs) {
        if (this.selectedOffsetDocs.length > 0) {
          const discard = await this.promptDiscardOffsetDoc();
          if (discard === 'Cancel') return;
        }
        this.$nextTick(() => {
          this.showAllDocs = false;
          this.selectedOffsetDocs = [];
        });
      }

      if (this.checkInputFields()) return;

      // Check if cheque No already exist in other receipts
      if (this.selectedPaymentType['Payment Type'] == 'CH' && this.chequeNo) {
        this.$fullscreenLoading('Validating Cheque');
        const chequeExists = await this.$apiCallV2('/Receipt/CheckRtChequeExists', { chqno: this.chequeNo });
        this.$fullscreenLoading();
        if (chequeExists.length > 0) {
          const receiptNoList = chequeExists.map((c) => c['ReceiptNo']);
          if (this.action == 'N' || (this.action == 'E' && !receiptNoList.includes(this.receiptNo))) {
            const keepCheque = await this.$showAlert(
              'warning',
              `Cheque No ${this.chequeNo} Already Exist in Another Receipt.`,
              `Receipt No: ${chequeExists[0]['ReceiptNo']}<br>Cheque Date: ${chequeExists[0]['ChqDate']}<br>Pay By: ${chequeExists[0]['billcu_no']}<br>Cheque Bank: ${chequeExists[0]['ChqBank'] || '-'}<br>Do you still want to proceed with this cheque no?`,
              [
                { text: 'Yes', color: 'green', textColor: 'white' },
                { text: 'No', color: 'red', textColor: 'white' },
              ],
            );
            if (keepCheque === 'No') {
              this.chequeNoErr = 'Cheque No already exist in another receipt';
              this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
              return;
            }
          }
        }
      }

      // Check cheque date, cannot be lesser than 6 months
      if (this.chequeDate && this.getMonthDifference(new Date(this.receiptDate), new Date(this.chequeDate)) <= -6) {
        const proceed = await this.$showAlert(
          'warning',
          'Please Verify Cheque Month',
          `Cheque Month: ${new Date(this.chequeDate).getMonth() + 1}/${new Date(this.chequeDate).getFullYear()}<br>Receipt Month: ${new Date(this.receiptDate).getMonth() + 1}/${new Date(this.receiptDate).getFullYear()}<br>Proceed?`,
          [
            { text: 'Yes', color: 'green', textColor: 'white' },
            { text: 'No', color: 'red', textColor: 'white' },
          ],
        );

        if (proceed == 'Cancel') {
          this.chequeDateErr = 'Cheque Date invalid!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          return;
        }
      }

      // Check if receipt amt lesser than balance
      if (Number(this.paymentAmt) < Number(this.receiptBalance)) {
        this.$showAlert(
          'error',
          'Payment Amount lesser than Receipt Balance',
          'Payment Amount cannot be lesser than Receipt Balance.',
        );
        return;
      }

      if (Number(this.totalOffset) > Number(this.paymentAmt)) {
        // Check if total offset more than receipt amt
        this.$showAlert(
          'error',
          'Receipt Amount less than Total Offset Amount',
          'Receipt amount cannot be lesser than total offset amount.',
        );
        return;
      } else if (Number(this.totalOffset) < Number(this.paymentAmt)) {
        // Check if receipt fully offset, if not show popup to confirm if continue
        const proceed = await this.$showAlert('warning', 'Receipt Not Fully Offset', 'Do you want to continue?', [
          { text: 'Yes', color: 'green', textColor: 'white' },
          { text: 'No', color: 'red', textColor: 'white' },
        ]);

        if (proceed == 'No') return;
      }
      //#endregion
      const allOffsets = this.deletedOffsetDetails.concat(this.offsetDetails);
      let tempOffsets = allOffsets.map((d) => {
        return {
          action: d['Action'] || '',
          seqno: d['SeqNo'] || '',
          invno: d['Doc No'] || '',
          jobno: d['Job No'] || '',
          offsetamt: Number(d['Offset Amount']).toFixed(2) || '0.00',
          foffsetamt: Number(d['F.Offset Amount']).toFixed(2) || '0.00',
          remarks: d['Remarks'] || '',
          currency: d['Currency'] || '',
          rate: d['Doc Rate'] || '1.00',
          txntype: d['Txn Type'] || '',
        };
      });
      tempOffsets = tempOffsets.filter((rec) => rec['action'] != '');

      const allAttachments = this.attachmentsList.concat(this.deletedAttList);
      const tempAtt = allAttachments.map((doc) => {
        return {
          doccode: 'ATT',
          docrefno: '',
          remarks: doc['Doc Remark'],
          status: '',
          docname: doc['File Name'],
          docimage: doc['Doc Image'],
          docimagetype: doc['File Type'],
          action: doc['Action'] ?? this.action,
          docguid: this.action == 'N' ? '' : doc['Document GUID'],
        };
      });

      const dataToSave = {
        action: this.action,
        receiptno: this.action == 'N' && this.disableReceiptNo ? '' : this.receiptNo,
        receipttype: this.selectedReceiptType ? this.selectedReceiptType['JobTypeID'] : '',
        receiptdate: this.receiptDate ? this.receiptDate.formatString('yyyy-MM-dd') : '',
        paymenttype: this.selectedPaymentType ? this.selectedPaymentType['Payment Type'] : '',
        billcu_no: this.selectedCustomer ? this.selectedCustomer['Customer Code'] : '',
        bank: this.selectedBank ? this.selectedBank['BankCode'] : '',
        txntype: this.selectedTxnType ? this.selectedTxnType['keyValue'] : '',
        paymentamt: this.paymentAmt || '0.00',
        fpaymentamt: this.paymentFAmt || '0.00',
        chqno: this.showChequeInfo ? this.chequeNo : '',
        chqdate: this.showChequeInfo && this.chequeDate ? this.chequeDate.formatString('yyyy-MM-dd') : '',
        chqbank: this.showChequeInfo ? this.chequeBank : '',
        bankchrg: this.bankCharge || '0.00',
        fbankchrg: this.bankFCharge || '0.00',
        remarks: this.remarks || '',
        remarks2: '',
        currency: this.selectedCurrency ? this.selectedCurrency['Currency Code'] : '',
        rate: this.exchRate,
        payeetype: '',
        payee: '',
        taxcode:
          this.selectedTxnType['keyValue'] == 'A' && this.selectedTaxCode ? this.selectedTaxCode['Tax Code'] : '',
        taxrate: this.selectedTxnType['keyValue'] == 'A' ? this.taxRate : '',
        taxamt: this.selectedTxnType['keyValue'] == 'A' ? this.taxAmt : '',
        ftaxamt: this.selectedTxnType['keyValue'] == 'A' ? this.fTaxAmt : '',
        reason: '',
        reinstate: '0',
        offsetdetails: tempOffsets ?? [],
        attachments: tempAtt ?? [],
      };

      this.$fullscreenLoading('Saving Receipt');
      const result = await this.$apiCallV2('/Receipt/SaveReceipt', dataToSave);
      this.$fullscreenLoading();

      if (result[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Receipt: ${result[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing Receipt: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      this.showOffsetDetailsList = false;
      this.showAttTable = false;
      this.clearAllData();
      const receiptno = result[0]['Receipt No'];
      this.$nextTick(async () => {
        await this.populateReceiptDetails(receiptno);
        this.loadSummaryOfEvents();
        this.showOffsetDetailsList = true;
        this.showAttTable = true;
        await this.refreshClicked();
      });

      this.successMessages.push(`${result[0]['ErrMsg']}`);
      this.viewMode = true;
      this.popupTitle = 'View Receipt';
      this.action = '';
    },

    // Get the difference in months between 2 dates (for cheque date)
    getMonthDifference: function (startDate, endDate) {
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth();

      return (endYear - startYear) * 12 + (endMonth - startMonth);
    },

    requiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    popupPrintClicked: function () {
      if (this.receiptDetails['Indicator'] == 'C') {
        this.$showAlert(
          'error',
          `Receipt ${this.receiptNo} Already Cancelled`,
          'You are not allowed to print cancelled receipt',
        );
        return;
      }
      this.openPrintReceipt(this.receiptNo);
    },

    clearAllData: function () {
      this.createdBy = '';
      this.updatedBy = '';
      this.receiptDetails = [];
      this.receiptNo = '';
      this.receiptDate = new Date();
      this.remarks = '';
      this.disableReceiptNo = true;
      this.selectedReceiptType = null;
      this.selectedCustomer = null;
      this.selectedPaymentType = null;
      this.selectedTxnType = null;
      this.selectedBank = null;
      this.chequeNo = '';
      this.chequeDate = null;
      this.chequeBank = '';
      this.selectedCurrency = null;
      this.exchRate = '';
      this.exchFactor = '';
      this.oldFAmt = '0.00';
      this.paymentFAmt = '0.00';
      this.oldAmt = '0.00';
      this.paymentAmt = '0.00';
      this.bankFCharge = '0.00';
      this.bankCharge = '0.00';
      this.selectedTaxCode = null;
      this.taxRate = '0';
      this.fTaxAmt = '0.00';
      this.taxAmt = '0.00';
      this.receiptBalance = '0.00';
      this.receiptFBalance = '0.00';

      this.showAllDocs = false;
      this.showOffsetInput = false;
      this.offsetDetails = [];
      this.selectedOffsetDetails = [];

      this.attachmentsList = [];
      this.selectedAtt = [];

      this.glEntryList = [];

      this.customerFieldValue = ''; //reset here so that each new or edit action should call the api to retrieve data again
      this.currencyFieldValue = '';

      this.clearAllOffsetDetails();
      this.clearAllAttData();
    },

    clearAllMessages: function () {
      this.requiredError = [];
      this.successMessages = [];
      this.errorMessages = [];
      this.receiptNoErr = '';
      this.receiptDateErr = '';
      this.receiptTypeErr = '';
      this.customerErr = '';
      this.paymentTypeErr = '';
      this.txnTypeErr = '';
      this.bankErr = '';
      this.chequeNoErr = '';
      this.chequeDateErr = '';
      this.chequeBankErr = '';
      this.currencyErr = '';
      this.exchRateErr = '';
      this.exchFactorErr = '';
      this.paymentFAmtErr = '';
      this.paymentAmtErr = '';
      this.bankFChargeErr = '';
      this.bankChargeErr = '';

      this.clearAllOffsetDetailsErr();
    },

    // Clear all data in offset details tab
    clearAllOffsetDetails: function () {
      this.selectedOffsetDocs = [];
      this.selectedDoc = null;
      this.offsetRemarks = '';
      this.docExchRate = '';
      this.docFOutstanding = '0.0000';
      this.docOutstanding = '0.0000';
      this.offsetFAmt = '0.0000';
      this.offsetAmt = '0.0000';
    },

    clearAllOffsetDetailsErr: function () {
      this.docNoErr = '';
      this.offsetFAmtErr = '';
      this.offsetAmtErr = '';
    },

    clearAllAttData: function () {
      this.documents = {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      };
      this.attachmentsRequiredErrorMessage = '';
    },

    getReceipt: async function (title) {
      this.popupTitle = title;
      if (this.selectedReceiptListing.length > 0) {
        const receiptNo = this.selectedReceiptListing[0]['Receipt No'];
        await this.populateReceiptDetails(receiptNo);
      }
    },

    populateReceiptDetails: async function (receiptNo) {
      this.$fullscreenLoading('Getting data');
      this.receiptDetails = [];
      this.receiptDetails = (
        await this.$apiCallV2('/Receipt/GetReceiptDetails', { receiptno: receiptNo, isMisc: '0' })
      )['Receipt']['ReceiptHeader'];
      this.selectedReceiptType = this.receiptTypeList.find((r) => r['JobTypeID'] == this.receiptDetails['Job Type']);
      this.receiptNo = this.receiptDetails['Receipt No'] || '';
      this.receiptDate =
        this.receiptDetails['Receipt Date'] ? new Date(this.receiptDetails['Receipt Date']) : new Date();
      this.createdBy = this.receiptDetails['Created By'] || '';
      this.updatedBy =
        this.receiptDetails['Updated By'] ?
          `${this.receiptDetails['Updated By']} ${new Date(this.receiptDetails['Date Updated']).formatString('yyyy-MM-dd HH:mm')}`
        : '';

      this.selectedCustomer = this.customerList.find((c) => c['Customer Code'] == this.receiptDetails['Customer No']);
      this.selectedPaymentType = this.paymentTypeList.find(
        (p) => p['Payment Type'] == this.receiptDetails['Payment Type'],
      );
      if (this.selectedPaymentType && this.selectedPaymentType['Payment Type'] == 'CH') {
        this.chequeNo = this.receiptDetails['Cheque No'] || '';
        this.chequeDate = this.receiptDetails['Cheque Date'] ? new Date(this.receiptDetails['Cheque Date']) : null;
        this.chequeBank = this.receiptDetails['Cheque Bank'] || '';
      }
      this.selectedTxnType = this.txnType.find((t) => t['keyValue'] == this.receiptDetails['Transaction Type']);
      this.selectedBank = this.bankList.find((b) => b['BankCode'] == this.receiptDetails['Bank']);
      this.remarks = this.receiptDetails['Remarks'] || '';

      this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == this.receiptDetails['Currency']);
      this.exchFactor = this.selectedCurrency?.['Factor'].toString() || '1';
      this.exchRate = this.receiptDetails['Rate'] || '1.0000';
      this.headerForeignCurrLossFocus(false, 'exchRate', this.exchRate);
      this.paymentFAmt = this.receiptDetails['F.Amount'] || '0.0000';
      this.paymentAmt = this.receiptDetails['Amount'] || '0.0000';
      this.bankFCharge = this.receiptDetails['F.Bank Charge'] || '0.0000';
      this.bankCharge = this.receiptDetails['Bank Charge'] || '0.0000';
      this.selectedTaxCode = this.taxCodeList.find((t) => t['Tax Code'] == this.receiptDetails['Tax Code']) ?? null;
      this.taxRate = this.receiptDetails['Tax Rate'] || '0';
      this.fTaxAmt = this.receiptDetails['Tax F.Amount'] || '0.0000';
      this.taxAmt = this.receiptDetails['Tax Amount'] || '0.0000';

      this.receiptBalance =
        this.receiptDetails['Balance'] ? this.$currencyFormat(this.receiptDetails['Balance']) : '0.00';
      this.receiptFBalance =
        this.receiptDetails['F.Balance'] ? this.$currencyFormat(this.receiptDetails['F.Balance']) : '0.00';

      this.offsetDetails = this.receiptDetails['Offsets'] ?? [];
      this.attachmentsList = this.receiptDetails['Attachments'] ?? [];
      this.glEntryList = this.receiptDetails['GL Entry'] ?? [];
      this.$fullscreenLoading();
    },

    getIndicatorName: function (key, value, type) {
      const item = this.summaryItems.find((item) => item['Title'] === key);
      if (item && item['Indicator']) {
        const indicator = item['Indicator'].find((i) => i['code'] === value);
        if (indicator) {
          return type == 'title' ? indicator['name'] : indicator['bg'];
        }
      }
   
      return item?.Indicator?.[0]?.name || null;
    },

    loadSummaryOfEvents: function () {
      for (const key in this.receiptDetails) {
        if (Object.prototype.hasOwnProperty.call(this.receiptDetails, key)) {

          const indicatorName = this.getIndicatorName(key, this.receiptDetails[key], 'title');
          const bgColour = this.getIndicatorName(key, this.receiptDetails[key], 'background');
          const element = document.getElementById(key);
  
          if (element) {
            element.textContent = indicatorName;
            element.style.backgroundColor = bgColour;
          }
        }
      }
    },

    headerForeignCurrLossFocus: async function (state, field, value) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (field) {
          case 'paymentFAmt': {
            if (this.offsetDetails.length > 0) {
              const totalOffset = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['F.Offset Amount']), 0);
              if (Number(value) < Number(totalOffset).toFixed(2)) {
                this.$refs['payamt'].setFocus(); // To prevent 'tab' keypress on focusing on next field
                await this.$showAlert(
                  'error',
                  'Payment Amount Invalid',
                  'Payment amount is less than total offset amount!',
                );
                this.paymentFAmt = this.oldFAmt;
                break;
              }
            }
            if (this.showAllDocs && Number(value) == 0 && this.selectedCurrency['Currency Code'] != 'MYR') {
              this.$refs['payamt'].setFocus(); // To prevent 'tab' keypress on focusing on next field
              await this.$showAlert(
                'error',
                'Payment Amount Invalid',
                'Payment amount is 0! Please enter amount before adding offset document.',
              );
              this.paymentFAmt = this.oldFAmt;
              break;
            }
            const rate = Number(this.exchRate) == 0 ? Number(this.oldRate) : Number(this.exchRate);
            this.oldFAmt = (Number(value) || 0).toFixed(2);
            this.paymentFAmt = (Number(value) || 0).toFixed(2);
            this.paymentAmt = (((Number(value) || 0) * Number(rate)) / Number(this.exchFactor)).toFixed(2);
            this.updateReceiptBalance();
            break;
          }

          case 'paymentAmt': {
            if (this.offsetDetails.length > 0) {
              const totalOffset = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['Offset Amount']), 0);
              if (Number(value) < parseFloat(totalOffset.toFixed(2))) {
                this.$refs['myramt'].setFocus(); // To prevent 'tab' keypress on focusing on next field
                await this.$showAlert(
                  'error',
                  'Local Payment Amount Invalid',
                  'Local payment amount is less than total offset amount!',
                );
                this.paymentAmt = this.oldAmt;
                break;
              }
            }
            if (this.showAllDocs && Number(value) == 0 && this.selectedCurrency['Currency Code'] == 'MYR') {
              this.$refs['myramt'].setFocus(); // To prevent 'tab' keypress on focusing on next field
              await this.$showAlert(
                'error',
                'Local Payment Amount Invalid',
                'Local payment amount is 0! Please enter amount before adding offset document.',
              );
              this.paymentAmt = this.oldAmt;
              break;
            }
            const rate = Number(this.exchRate) == 0 ? Number(this.oldRate) : Number(this.exchRate);
            this.oldAmt = (Number(value) || 0).toFixed(2);
            this.paymentAmt = (Number(value) || 0).toFixed(2);
            this.paymentFAmt = (((Number(value) || 0) / Number(rate)) * Number(this.exchFactor)).toFixed(2);
            this.updateReceiptBalance();
            break;
          }

          case 'exchRate':
            this.exchRate = (parseFloat(value) || 1).toFixed(4);
            if (value <= 0) {
              await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
              this.exchRate = parseFloat(this.oldRate).toFixed(4);
            }
            this.paymentAmt = ((Number(this.paymentFAmt) * Number(this.exchRate)) / Number(this.exchFactor)).toFixed(2);
            if (Number(this.bankCharge) > 0) {
              this.autoCalculateBankChrg(this.paymentAmt);
            }
            break;

          case 'fBankChrg':
            this.bankFCharge = (parseFloat(value) || 0).toFixed(2);
            this.bankCharge = ((Number(value) || 0) * Number(this.exchRate)).toFixed(2);
            break;

          case 'bankChrg':
            this.bankCharge = (parseFloat(value) || 0).toFixed(2);
            if (!this.disableFBankChrg) {
              this.bankFCharge = ((Number(value) || 0) / Number(this.exchRate)).toFixed(2);
            }
            break;

          case 'taxRate':
            this.taxAmt = (Number(this.paymentAmt) * (Number(value) / 100)).toFixed(2);
            this.fTaxAmt = (Number(this.paymentFAmt) * (Number(value) / 100)).toFixed(2);
            break;
        }
      }
    },

    // To calculate local bank charge amount
    autoCalculateBankChrg: function (amount) {
      if (!this.viewMode) {
        let charge = '0.0000';
        if (Number(amount) > 0) {
          const calc = (Number(amount) * 0.03) / 100;
          charge = calc < 0.5 ? 0.5 : calc.toFixed(2);
        }
        this.headerForeignCurrLossFocus(false, 'bankChrg', charge);
      }
    },

    updateReceiptBalance: function () {
      const totalOffset = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['Offset Amount']), 0);
      const bal = Number(this.paymentAmt) - totalOffset;
      this.receiptBalance = this.$currencyFormat(bal);

      const totalFOffset = this.offsetDetails.reduce((acc, amt) => acc + Number(amt['F.Offset Amount']), 0);
      const fBal = Number(this.paymentFAmt) - totalFOffset;
      this.receiptFBalance = this.$currencyFormat(fBal);
    },

    //#region Offset Details Tab Functions
    getOffsetDocsListing: async function () {
      if (!this.selectedCustomer) return;
      const dataToPost = {
        receiptno: this.action == 'N' ? '' : this.receiptNo,
        billcu: this.selectedCustomer ? this.selectedCustomer['Customer Code'] : '',
        currency: this.selectedCurrency ? this.selectedCurrency['Currency Code'] : 'MYR',
      };
      this.offsetDocsListing = [];
      const allDocs = (await this.$apiCallV2('/Receipt/GetOffsetDocListing', dataToPost)) ?? [];
      this.offsetDocsListing = allDocs.map((doc) => {
        doc['Offset Amount'] = doc['Outstanding'];
        doc['F.Offset Amount'] = doc['F.Outstanding'];
        doc['Remarks'] = '';
        return {
          ...doc,
        };
      });
    },

    openOffsetDocsSelection: async function () {
      if (!this.selectedCustomer) {
        await this.$showAlert(
          'warning',
          'No Customer is Selected',
          'Please select a customer to add offset documents.',
        );
        return;
      }
      if (!this.selectedCurrency) {
        await this.$showAlert(
          'warning',
          'No Currency is Selected',
          'Please select a currency to add offset documents.',
        );
        return;
      }

      if (
        (this.selectedCurrency && this.selectedCurrency['Currency Code'] == 'MYR' && Number(this.paymentAmt) <= 0) ||
        (this.selectedCurrency && this.selectedCurrency['Currency Code'] != 'MYR' && Number(this.paymentFAmt) <= 0)
      ) {
        await this.$showAlert('warning', 'Payment Amount is 0', 'Please enter payment amount first.');
        return;
      }

      if (this.customerFieldValue !== this.selectedCustomer['Code - Customer'] || this.currencyFieldValue !== this.selectedCurrency['Currency Code']) {
          this.$fullscreenLoading('Getting Offset Docs Listing');
          await this.getOffsetDocsListing();
          this.customerFieldValue = this.selectedCustomer['Code - Customer']; //update the customer currency field value here only when got call the api
          this.currencyFieldValue = this.selectedCurrency['Currency Code'];
          this.$fullscreenLoading();
      }
      this.showAllDocs = true;
      this.showOffsetInput = false;
    },

    offsetDetailsTableButtonClicked: async function (_btn, data) {
      this.editFromOffsetListing = false;
      this.showOffsetInput = true;
      if (this.customerFieldValue !== this.selectedCustomer['Code - Customer'] || this.currencyFieldValue !== this.selectedCurrency['Currency Code']) {
        this.$fullscreenLoading('Getting Offset Docs Listing');
        await this.getOffsetDocsListing();
        this.customerFieldValue = this.selectedCustomer['Code - Customer']; //update the customer currency field value here only when got call the api
        this.currencyFieldValue = this.selectedCurrency['Currency Code'];
        this.$fullscreenLoading();
      }
      this.selectedDoc = this.offsetDocsListing.find((d) => d['Offset Doc'] == data['Doc No']);
      this.docExchRate = Number(data['Doc Rate']).toFixed(4);
      this.docFOutstanding = Number(data['F.Outstanding']).toFixed(2);
      this.docOutstanding = Number(data['Outstanding']).toFixed(2);
      this.offsetAmt = Number(data['Offset Amount']).toFixed(2);
      this.offsetFAmt = Number(data['F.Offset Amount']).toFixed(2);
      this.offsetRemarks = data['Remarks'] || '';
    },

    selectDeselectOffsetDocsClicked: function () {
      if (this.selectedOffsetDocs.length == this.filterOffsetDocsListing.length) {
        this.selectedOffsetDocs = [];
      } else {
        this.selectedOffsetDocs = [...this.filterOffsetDocsListing];
      }
    },

    addMultiOffsetDocs: function () {
      if (Number(this.totalSelectedOffsetFAmt.replaceAll(',', '')) > Number(this.receiptFBalance.replaceAll(',', ''))) {
        this.$showAlert(
          'warning',
          'Total Selected Amount Exceeded Receipt Balance',
          `Total selected amount cannot exceed receipt balance ${this.selectedCurrency['Currency Code']} ${this.receiptFBalance}.`,
        );
        return;
      }
      const temp = this.offsetDetails;
      let highestSeq = temp.reduce((max, d) => Math.max(max, Number(d['SeqNo']) || 0), 0);
      for (const doc of this.selectedOffsetDocs) {
        highestSeq++;
        const dataToAdd = {
          Action: 'N',
          'Doc No': doc['Offset Doc'],
          'Offset Amount': doc['Offset Amount'],
          'F.Offset Amount': doc['F.Offset Amount'],
          'Job No': doc['Job No'] || '',
          'Doc Rate': doc['Doc Rate'],
          Currency: doc['Currency'],
          Remarks: doc['Remarks'] ?? '',
          'Txn Type': doc['Txn Type'],
          'User ID': '',
          'Date Updated': '',
          Outstanding: doc['Outstanding'],
          'F.Outstanding': doc['F.Outstanding'],
          SeqNo: highestSeq,
        };
        dataToAdd['Offset Amount'] = Number(doc['F.Offset Amount']) * Number(this.exchRate);
        temp.push(dataToAdd);
      }

      this.showOffsetDetailsList = false;
      this.showAllDocs = false;
      // Revert offset amount to outstanding after add (incase there's offset amount edited by user)
      // If delete offset detail, user should be seeing original offset amount instead of edited (in offset doc listing)
      this.offsetDocsListing.forEach((doc) => {
        doc['Offset Amount'] = doc['Outstanding'];
        doc['F.Offset Amount'] = doc['F.Outstanding'];
      });
      this.$nextTick(() => {
        this.offsetDetails = temp;
        this.showOffsetDetailsList = true;
        this.selectedOffsetDocs = [];
        this.updateReceiptBalance();
        this.clearAllOffsetDetails();
        this.clearAllOffsetDetailsErr();
      });
    },

    cancelSelectMultiDocs: function () {
      this.showAllDocs = false;
      this.selectedOffsetDocs = [];
    },

    // When user clicks on "Edit Offset" button at the offset documents listing table box, show input fields to edit that selected doc's offset
    editOffsetAmtBtnClicked: function (_btn, data) {
      this.showOffsetInput = true;
      this.showAllDocs = false;
      this.editFromOffsetListing = true;

      this.selectedDoc = this.offsetDocsListing.find((d) => d['Offset Doc'] == data['Offset Doc']);
      this.docExchRate = Number(this.selectedDoc?.['Doc Rate'] || '1.0000').toFixed(4);
      this.docFOutstanding = Number(data['F.Outstanding']).toFixed(2);
      this.docOutstanding = Number(data['Outstanding']).toFixed(2);
      this.offsetAmt = Number(data['Offset Amount']).toFixed(2);
      this.offsetFAmt = Number(data['F.Offset Amount']).toFixed(2);
      this.offsetRemarks = data['Remarks'] || '';
    },

    // If user editing offset amount from offset documents list (table in add offset documents), update the offset amount in the list (editFromOffsetListing = true)
    // If user edits offset amount from receipt offset details, update the offset amount in the details table list (editFromOffsetListing = false)
    editOffsetClicked: async function () {
      // Check if offset amt
      if (
        ['IN', 'DN'].includes(this.selectedDoc['Txn Type']) ||
        (this.selectedDoc['Txn Type'] == 'JL' && Number(this.offsetAmt) >= 0)
      ) {
        if (
          // IN and DN amount are stated in positive, so offset cannot be more than outstanding
          (this.selectedCurrency['Currency Code'] == 'MYR' && Number(this.docOutstanding) < Number(this.offsetAmt)) ||
          (this.selectedCurrency['Currency Code'] != 'MYR' && Number(this.docFOutstanding) < Number(this.offsetFAmt))
        ) {
          await this.$showAlert(
            'error',
            'Offset Amount Exceeded Outstanding',
            'Offset amount cannot exceed outstanding amount.',
          );
          return;
        }
      } else if (
        ['CN', 'RT'].includes(this.selectedDoc['Txn Type']) ||
        (this.selectedDoc['Txn Type'] == 'JL' && Number(this.offsetAmt) < 0)
      ) {
        if (
          // CN and RT amount are stated in negative, so offset cannot be less than outstanding
          (this.selectedCurrency['Currency Code'] == 'MYR' && Number(this.docOutstanding) > Number(this.offsetAmt)) ||
          (this.selectedCurrency['Currency Code'] != 'MYR' && Number(this.docFOutstanding) > Number(this.offsetFAmt))
        ) {
          await this.$showAlert(
            'error',
            'Outstanding Exceeded Offset Amount',
            'Outstanding amount cannot exceed offset amount.',
          );
          return;
        }
      }

      if (this.editFromOffsetListing) {
        const matching = this.offsetDocsListing.find((doc) => doc['Offset Doc'] == this.selectedDoc['Offset Doc']);
        if (matching) {
          matching['Offset Amount'] = this.offsetAmt;
          matching['F.Offset Amount'] = this.offsetFAmt;
          matching['Remarks'] = this.offsetRemarks || '';
        }
        this.showOffsetInput = false;
        this.showAllDocs = true;
        const temp = this.selectedOffsetDocs.map((doc) => doc['Offset Doc']);
        this.selectedOffsetDocs = this.offsetDocsListing.filter((doc) => temp.includes(doc['Offset Doc']));
      } else {
        const temp = this.offsetDetails;
        const matching = temp.find((doc) => doc['Doc No'] == this.selectedDoc['Offset Doc']);
        if (matching) {
          matching['Action'] = matching['Action'] || 'E';
          matching['Offset Amount'] = this.offsetAmt;
          matching['F.Offset Amount'] = this.offsetFAmt;
          matching['Remarks'] = this.offsetRemarks || '';
          matching['User ID'] = '';
          matching['Date Updated'] = '';
        }
        this.showOffsetInput = false;
        this.offsetDetails = [];
        this.showOffsetDetailsList = false;
        this.$nextTick(() => {
          this.offsetDetails = temp;
          this.showOffsetDetailsList = true;
          this.updateReceiptBalance();
          this.clearAllOffsetDetails();
          this.clearAllOffsetDetailsErr();
        });
      }
    },

    cancelOffsetClicked: function () {
      this.showAllDocs = this.editFromOffsetListing;
      this.showOffsetInput = false;
    },

    deleteOffset: async function () {
      if (this.selectedOffsetDetails.length == 0) {
        await this.$showAlert(
          'error',
          'No Offset Details Selected',
          'Please select at least 1 offset detail to delete.',
        );
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Offset Record',
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let temp = [];
      if (this.selectedOffsetDetails[0]['Action'] && this.selectedOffsetDetails[0]['Action'] == 'N') {
        temp = this.offsetDetails.filter((doc) => doc['Doc No'] != this.selectedOffsetDetails[0]['Doc No']);
      } else {
        this.offsetDetails.forEach((doc) => {
          if (doc['Doc No'] == this.selectedOffsetDetails[0]['Doc No']) {
            doc['Action'] = 'D';
            this.deletedOffsetDetails.push(doc);
          }
        });
        temp = this.offsetDetails.filter((doc) => doc['Action'] != 'D');
      }

      this.offsetDetails = [];
      this.showOffsetDetailsList = false;
      this.$nextTick(() => {
        this.offsetDetails = temp;
        this.selectedOffsetDetails = [];
        this.clearAllOffsetDetails();
        this.clearAllOffsetDetailsErr();
        this.showOffsetDetailsList = true;
        this.updateReceiptBalance();
      });
    },

    offsetFocusChanged: function (state, field, value) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (field) {
          case 'famt':
            this.offsetFAmt = parseFloat(value).toFixed(2);
            this.offsetAmt = ((Number(value) * Number(this.exchRate)) / Number(this.exchFactor)).toFixed(2);
            break;
          case 'amt':
            this.offsetAmt = parseFloat(value).toFixed(2);
            this.offsetFAmt = ((Number(value) / Number(this.exchRate)) * Number(this.exchFactor)).toFixed(2);
            break;
        }
      }
    },
    //#endregion

    //#region Attachment Tab Functions
    openAttachmentPopup: function (action) {
      this.clearAllAttData();
      switch (action) {
        case 'new':
          this.attachmentsAction = 'N';
          this.selectedAtt = [];
          this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Add Attachment';
          break;

        case 'edit':
          if (this.selectedAtt.length == 0) {
            this.$showAlert('error', 'No Attachment Selected', 'Please select an Attachment to edit');
            return;
          }
          this.attachmentsAction = 'E';
          this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Edit Attachment';

          this.documents = {
            docName: this.selectedAtt[0]['File Name'],
            attachementFile: this.selectedAtt[0]['Doc Image'],
            filetype: this.selectedAtt[0]['File Type'],
            docGuid: this.selectedAtt[0]['Document GUID'],
            remarks: this.selectedAtt[0]['Doc Remark'],
            docID: this.selectedAtt[0]['ID'],
          };
          break;
      }
      this.showAttachmentsPopup = true;
    },

    deleteAttachmentClicked: async function () {
      if (this.selectedAtt.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Attachment',
        `Are you sure you want to delete this attachment ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let existingData = false;
      const tempDeleted = this.deletedAttList;
      this.deletedAttList = [];

      for (const d of this.selectedAtt) {
        const doc = {
          Action: 'D',
          'Document GUID': d['Document GUID'],
        };

        if (d['Document GUID']) {
          tempDeleted.push(doc);
          existingData = true;
        }
      }

      const temp = this.attachmentsList;
      const index = temp.findIndex((item) => {
        return existingData ?
            item['Document GUID'] == this.selectedAtt[0]['Document GUID']
          : item['ID'] == this.selectedAtt[0]['ID'];
      });
      temp.splice(index, 1);
      this.attachmentsList = [];

      this.$nextTick(() => {
        this.deletedAttList = tempDeleted;
        this.attachmentsList = temp;
        this.selectedAtt = [];
      });
    },

    attAdditionalBtnsClicked: async function (btn, data) {
      switch (btn) {
        case 'View':
          this.attachmentsAction = 'V';
          this.selectedAtt = [data];
          await this.projectAttachment({
            'File Name': data['File Name'],
            'MIME Type': null,
            'File Data': data['Doc Image'],
          });
          break;
      }
    },

    attachmentsPopupBtnsClicked: async function (btn) {
      let temp;
      let highestID = 1;
      let doc = null;

      switch (btn) {
        case 'Add':
          if (!this.documents['docName']) {
            this.attachmentsRequiredErrorMessage = 'Please attach at least one file';
            return;
          }

          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (d['File Name'] === this.documents['docName']) duplicate = true;
            });

            if (duplicate) {
              await this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }

          temp = [...this.attachmentsList];
          this.attachmentsList = [];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          doc = {
            Action: 'N',
            'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
            'Doc Date': new Date().formatString('yyyy-MM-dd'),
            'File Name': this.documents['docName'] ? this.documents['docName'] : '',
            'File Type': this.documents['filetype'] ? this.documents['filetype'] : '',
            'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
            'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
            ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            _attachmentAction: 'New',
          };

          this.$nextTick(() => {
            temp.push(doc);
            this.attachmentsList = temp;
            this.oldDoc = this.documents['docName'];
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;

        case 'Edit': {
          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (
                d['File Name'] === this.documents['docName'] &&
                this.documents['docName'] != this.selectedAtt[0]['File Name']
              )
                duplicate = true;
            });

            if (duplicate) {
              this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }
          temp = [...this.attachmentsList];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          let existingData = false;
          for (const d of this.selectedAtt) {
            doc = {
              Action: d['Action'] ? d['Action'] : 'E',
              'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
              'Doc Date': new Date().formatString('yyyy-MM-dd'),
              'File Name': this.documents['docName'] ? this.documents['docName'] : '',
              'File Type': this.documents['filetype'] ? this.documents['filetype'] : '',
              'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
              'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
              ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            };

            if (d['Document GUID']) existingData = true;
          }

          const temp2 = this.attachmentsList;
          const index = temp2.findIndex((item) => {
            return existingData ?
                item['Document GUID'] == this.selectedAtt[0]['Document GUID']
              : item['ID'] == this.selectedAtt[0]['ID'];
          });

          temp2.splice(index, 1, doc);
          this.attachmentsList = [];
          this.$nextTick(() => {
            this.oldDoc = this.documents['docName'];
            this.attachmentsList = temp2;
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;
        }

        case 'Cancel':
          this.showAttachmentsPopup = false;
          this.clearAllAttData();
          break;
      }
    },

    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['filetype'] = '';
        this.documents['attachmentFile'] = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.documents['docName'] = file[0].name;
        this.documents['filetype'] = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.attachmentsRequiredErrorMessage = '';
        this.$fullscreenLoading();
      }
    },

    projectAttachment: async function (file) {
      if (!file && process.env['NODE_ENV'] == 'development') {
        throw new Error('No file was passed to projectAttachment. Check for logic errors');
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/Receipt/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );

        const untypedByteArray = [];
        for (let i = 0; i < byteCharacters.length; i++) {
          untypedByteArray.push(byteCharacters.charCodeAt(i));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: this.attachmentProjectorType });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },

    attachmentProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        return false;
      }

      const fileNameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = fileNameSplit[fileNameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },

    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedAtt[0]['File Name'];
      anchor.click();
    },

    closeProjector: function () {
      this.attachmentProjectorType = '';
      this.attachmentProjectorData = '';
      this.showAttachmentProjector = false;
    },
    //#endregion

    // Print Receipt
    openPrintReceipt: function (rtno) {
      let printRtRoute;
      if (rtno) {
        printRtRoute = this.$router.resolve(
          `/Home/${this.$route.params.user}/FINAR/Print Receipt?rtno=${rtno.trim?.() ?? ''}&type=0`,
        );
      } else {
        printRtRoute = this.$router.resolve(`/Home/${this.$route.params.user}/FINAR/Print Receipt?type=0`);
      }
      window.open(printRtRoute.href, '_blank');
    },

    // Behaviour when click buttons in delete popup
    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete': {
          if (!this.deleteReceiptRemarks.trim()) {
            this.deleteReceiptRemarksErr = 'Please enter delete remarks';
            return;
          }

          this.$fullscreenLoading('Deleting Selected Receipt');
          const deleteBody = {
            action: this.action,
            receiptno: this.selectedReceiptListing[0]['Receipt No'],
            reason: this.deleteReceiptRemarks || '',
            reinstate: '0',
          };

          const deleteResult = await this.$apiCallV2('/Receipt/SaveReceipt', deleteBody);
          this.$fullscreenLoading();
          this.showDeleteReceipt = false;

          if (deleteResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `Receipt ${this.selectedReceiptListing[0]['Receipt No']} was successfully deleted.`,
            );
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked();
            });
          } else {
            await this.$showAlert('error', 'Delete Unsuccessful', `${deleteResult[0]['ErrMsg']}`);
            this.receiptListing = [];
            this.selectedReceiptListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked();
            });
          }
          break;
        }

        case 'Cancel':
          this.deleteReceiptRemarks = '';
          this.showDeleteReceipt = false;
          break;
      }
    },

    //#region InputQuery/Dropdown Selected Functions
    receiptTypeSelected: function () {
      this.viewMode = false;
    },
    paymentTypeSelected: function (newPaymentType) {
      if (newPaymentType['Payment Type'] == 'CH' && this.defaultChqBank.trim()) {
        this.selectedBank = this.bankList.find((b) => b['BankCode'] == this.defaultChqBank);
      } else if (newPaymentType['Payment Type'] == 'CS' && this.defaultCashBank.trim()) {
        this.selectedBank = this.bankList.find((b) => b['BankCode'] == this.defaultCashBank);
      }
      this.bankSelected(this.selectedBank);

      if (newPaymentType['Payment Type'] != 'CH') {
        const errTemp = this.requiredError.filter((err) => ![this.chequeNoErr, this.chequeDateErr].includes(err.value));
        this.requiredError = errTemp;
        this.chequeNoErr = '';
        this.chequeDateErr = '';
      }
    },

    bankSelected: function (newBank) {
      if (newBank && this.offsetDetails.length == 0) {
        const currentCurrency = this.selectedCurrency['Currency Code'];
        this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == (newBank['BankCurrency'] || 'MYR'));
        // If currency if different than before, then only call currencySelected (will refresh offset docs list)
        if (currentCurrency != this.selectedCurrency['Currency Code']) {
          this.currencySelected(this.selectedCurrency);
        }
      }
    },

    currencySelected: async function (newCurrency) {
      this.exchRate = parseFloat(newCurrency?.['Currency Rate']).toFixed(4) || 1.0;
      if (newCurrency && Number(newCurrency['Currency Rate']) == 0) {
        await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
        this.exchRate = '1.0000';
      }
      this.exchFactor = newCurrency?.['Factor'].toString() || '1';
      this.headerForeignCurrLossFocus(false, 'exchRate', this.exchRate);
      this.updateReceiptBalance();

      // if (!oldCurrency || (oldCurrency && newCurrency['Currency Code'] != oldCurrency['Currency Code'])) {
      //   this.$fullscreenLoading('Getting Offset Documents');
      //   await this.getOffsetDocsListing();
      //   this.$fullscreenLoading();
      // }
    },

    taxCodeSelected: function (newTaxCode) {
      this.taxRate = newTaxCode ? newTaxCode['RATE'].toString() : '0';
      this.headerForeignCurrLossFocus(false, 'taxRate', this.taxRate);
    },

    docSelected: function (newDoc) {
      this.docExchRate = newDoc['Doc Rate'] || this.exchRate;
      this.docFOutstanding = Number(newDoc['F.Outstanding']).toFixed(2);
      this.docOutstanding = Number(newDoc['Outstanding']).toFixed(2);
    },
    //#endregion
  },
  async mounted() {
    let sas = [];
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Receipt') {
        sas = x['data'];
      }
    });

    const mountedPromises = [];
    let mountedPromisesFulFilledCount = 0;

    mountedPromises.push(
      this.$apiCallV2('/Receipt/GetJobTypeConfig', { doctype: 'R' }).then((result) => {
        this.receiptTypeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLCUST' }).then((result) => {
        this.customerList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PAYMENTTYPE' }).then((result) => {
        this.paymentTypeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BANK' }).then((result) => {
        this.bankList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ARTAXCODE' }).then((result) => {
        this.taxCodeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=RECEIPTNOAUTO').then((result) => {
        this.disableReceiptNo = result[0].SettingsValue == 'Y';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_AllowEditRT').then((result) => {
        this.allowEditRt = result[0].SettingsValue == 'Y';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=DefaultBankRtCash').then((result) => {
        this.defaultCashBank = result[0].SettingsValue || '';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=DefaultBankRtChq').then((result) => {
        this.defaultChqBank = result[0].SettingsValue || '';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(async (_promiseOutcomes) => {
      await this.refreshClicked();
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: menu['key'] != 'mnuReinStateReceipt',
      }));

      sas.forEach((k) => {
        const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
        if (selectedMenu) {
          selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] != 'False';
        }
      });
    });

    if (this.$route.query['rtno']) {
      this.filterReceiptBySearch = true;
      this.filterReceiptByDate = false;
      this.selectedSearchBy = this.searchFilterItems.getSelected('Receipt #', 'Search By');
      this.selectedSearchCriteria = this.searchCriteriaItems.getSelected('EQUALS', 'name');
      this.searchValue = this.$route.query['rtno'];
      await this.refreshClicked();
      this.selectedReceiptListing = this.receiptListing.filter((r) => r['Receipt No'] === this.$route.query['rtno']);
      // SAS Control
      const menuTitle = this.$route.query['action'] == 'E' ? 'Edit Receipt' : 'View Receipt';
      const selectedMenu = this.menu.getSelected(menuTitle, 'title');
      if (!selectedMenu['enabled']) {
        this.$showAlert('warning', 'Access Denied', `You do not have permission to ${menuTitle}`);
        return;
      }
      this.$nextTick(() => {
        this.menuClicked({ 
          title: menuTitle 
        });
      });
    }

    const cocode = (await this.$apiCallV2('/Login/GetDetails'))?.CoCode;
    const companyDetails = (
      await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'COMPANY', recordid: cocode })
    )['Company'];
    this.companyGSTEnabled = companyDetails?.CompanyGST2Enabled;
    this.companyGSTEffectiveDate = companyDetails?.CompanyGST2EffectiveDate;
    this.companySSTEffectiveDate = companyDetails?.CompanyGSTEffectiveDate;

    // Mapping for input query and dropdown
    this.customerList = this.customerList.map((c) => {
      return {
        ...c,
        'Code - Customer': `${c['Customer Code']} - ${c['Customer Name']}`,
      };
    });

    this.paymentTypeList = this.paymentTypeList.map((p) => {
      return {
        ...p,
        'Payment Type - Description': `${p['Payment Type']} - ${p['Description']}`,
      };
    });

    this.bankList = this.bankList.map((b) => {
      return {
        ...b,
        'BankCode - BankName': `${b['BankCode']} - ${b['BankName']}`,
      };
    });

    this.taxCodeList = this.taxCodeList.map((t) => {
      return {
        ...t,
        'Tax Code - Description': `${t['Tax Code']} - ${t['Description']}`,
      };
    });
  },
  watch: {
    selectedCurrency: function (val) {
      this.oldRate = val ? val['Currency Rate'] : '';
    },

    exchRate: function (newVal, oldVal) {
      if (Number(oldVal) != Number(newVal)) {
        this.oldRate = Number(oldVal).toFixed(4);
      }
    },

    paymentFAmt: function (newVal, oldVal) {
      if (this.offsetDetails.length > 0) {
        const totalOffset = this.offsetDetails.reduce((acc, doc) => acc + Number(doc['F.Offset Amount']), 0);
        if (
          (Number(newVal) === 0 && this.showAllDocs && Number(oldVal) !== 0) ||
          (Number(oldVal) >= totalOffset && Number(newVal) < totalOffset)
        ) {
          this.oldFAmt = Number(oldVal || 0).toFixed(2);
        }
      }
    },

    paymentAmt: function (newVal, oldVal) {
      if (this.offsetDetails.length > 0) {
        const totalOffset = this.offsetDetails.reduce((acc, doc) => acc + Number(doc['Offset Amount']), 0);
        if (
          (Number(newVal) === 0 && this.showAllDocs && Number(oldVal) !== 0) ||
          (Number(oldVal) >= totalOffset && Number(newVal) < totalOffset)
        ) {
          this.oldAmt = Number(oldVal || 0).toFixed(2);
        }
      }
    },

    selectedBank: function (val) {
      if (val && val['BankCurrency'] == 'MYR') {
        this.bankFCharge = '0.00';
      }
    },

    selectedReceiptListing: function (val) {
      if (val.length > 0 && val[0]['Indicator'] === 'C') {
        this.menu.find((m) => m['key'] == 'mnuReinStateReceipt')['enabled'] = true;
      } else {
        this.menu.find((m) => m['key'] == 'mnuReinStateReceipt')['enabled'] = false;
      }

      if (val.length > 0 && val[0]['Authorized'] === 'A') {
        this.menu.find((m) => m['key'] == 'mnUnAuthorized')['visible'] = true;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['visible'] = false;
      } else {
        this.menu.find((m) => m['key'] == 'mnUnAuthorized')['visible'] = false;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['visible'] = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.rcp-main {
  display: flex;
  font-size: 12px;
  padding: 10px 45px;

  > .left-menu {
    display: flex;
    justify-content: center;

    > .loading-bar-container {
      border: 1px solid rgba(0, 0, 0, 0.2);
      flex-grow: 1;
      height: 15px;
      position: absolute;
      bottom: 15px;
      z-index: 101;

      > .loading-bar-text {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }

      > .loading-bar {
        height: 100%;
        background-color: rgb(60, 211, 0);
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        z-index: 9;
      }
    }
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-desc {
        padding-right: 8px;
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px);
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.receipt-entries {
  height: calc(100% - 27px);
  width: 100%;
  overflow: scroll;

  .receipt-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .header-containers {
      width: 100%;
      margin-right: 5px;
    }
  }
}

.summary-row {
  display: flex;
  flex-direction: row;
  width: fit-content;

  > :nth-child(1) {
    margin-right: 5px;
  }
}

.summary-block {
  position: relative;
  margin-bottom: 2px;
  margin-right: 3px;

  > .summary-item {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 120px;
    min-width: 120px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }
  > .summary-item-printer {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 100px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }

  > .milestone-tooltip {
    position: absolute;
    text-overflow: wrap;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
}

.legend-items {
  display: flex;
  align-items: center;
  margin-right: 3px;

  > .legend-box {
    height: 10px;
    width: 10px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    margin-right: 3px;
  }
}

.foreign-currency-main {
  font-size: 12px;
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: rgb(225, 255, 255);
}

.foreign-currency-controls {
  position: relative;
  z-index: 101;
}

.foreign-currency-row {
  position: relative;
  display: flex;
  align-items: center;
  // min-height: 30px;
  flex-direction: row;

  > :not(:first-child) {
    margin-left: 5px;
  }

  > .foreign-currency-column {
    flex: 1;
  }
}

.foreign-currency-background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  z-index: 1;

  > .background-left {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    // background-color: transparent;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-left: 3px solid white;
  }
}

.tab-slot {
  display: flex;
  height: 100%;

  > .tab-details-styling {
    margin-right: 3px;
    height: 100%;
    overflow: scroll;

    .tab-buttons {
      display: flex;
      flex-direction: row;
      margin: 5px 5px;
      align-items: center;

      > button {
        margin-right: 5px;
        height: fit-content;
      }
    }
  }

  > .side-entry-open {
    height: 100%;
    border-left: 1px solid black;
    overflow: scroll;
    margin-left: 5px;
    padding-left: 10px;

    .title-button {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      height: 50px;
      margin: 5px 0px;
      padding: 5px 0px;
      // position: sticky;
      // top: 0;
      // z-index: 5;
      // background-color: white;

      .buttons-arr {
        display: flex;

        button {
          margin-right: 5px;
          height: fit-content;
        }
      }
    }
  }
}

.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  padding: 5px 10px;
  align-content: space-evenly;
  min-width: 210px;
  padding-right: 5px;
  display: inline;
  text-align: right;
}
</style>
